import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import SkillEditorBody from '../../components/SkillEditorBody';
import AgentBundlePreview from '../../components/AgentBundlePreview';
import McpDetailBody from '../../components/McpDetailBody';
import { extractSkillDescription } from '../../utils/skillFrontmatter';
import AgentDraftPreview from '../../components/AgentDraftPreview';
import AgentShareMenuModal from '../../components/AgentShareMenuModal';
import ToolShareMenuModal from '../../components/ToolShareMenuModal';
import { resolveAgentShare, shareAgent, previewAgentShare, listDwAgents, type AgentShareSnapshot, type DwAgentRecord, } from '../claudeagents/claudeAgentApi';
import { ROUTES } from '../../app/routes';
import { upsertMyTool as apiUpsertTool, submitPublishRequest as apiSubmitPublishRequest, installTool as apiInstallTool, installToolForAgency as apiInstallToolForAgency, installAgentForAgency as apiInstallAgentForAgency, summarizeAgentAgencyInstall, createShare, getShares, revokeShare, regenerateShare, type Share, type ShareScope, } from '../../services/myToolsApi';
import '../../styles/MyTools.css';
import '../../styles/AmpsGridPage.css';
function authHeaders(): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    };
}
type ToolType = 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
type Visibility = 'just_me' | 'my_agency' | 'published';
type WizardView = 'create' | 'installed' | 'creations' | 'marketplace';
const TOOL_TYPE_TO_INT: Record<ToolType, number> = {
    skill: 0, agent: 1, workflow: 2, widget: 3, mcp_server: 4, integration: 5,
};
const INT_TO_TOOL_TYPE: Record<number, ToolType> = {
    0: 'skill', 1: 'agent', 2: 'workflow', 3: 'widget', 4: 'mcp_server', 5: 'integration',
};
const VISIBILITY_TO_INT: Record<Visibility, number> = {
    just_me: 0, my_agency: 1, published: 2,
};
const INT_TO_VISIBILITY: Record<number, Visibility> = {
    0: 'just_me', 1: 'my_agency', 2: 'published',
};
type PublishStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested';
interface MyTool {
    id: string;
    type: ToolType;
    name: string;
    category: string;
    icon: string;
    description: string;
    skills: string;
    kbFileNames: string[];
    visibility: Visibility;
    createdAt: string;
    publishStatus?: PublishStatus;
    publishRequestId?: string;
    isAgencyInstalled?: boolean;
    mcpUrl?: string;
    mcpHeaders?: McpHeaderField[];
    skillContent?: string;
    agentShareToken?: string;
}
type InstallableTool = Pick<MyTool, 'id' | 'name' | 'type' | 'icon' | 'mcpHeaders' | 'visibility' | 'description' | 'mcpUrl'>;
type FieldScope = 'shared' | 'per_user';
interface ConfigField {
    key: string;
    label: string;
    type: 'text' | 'toggle' | 'select';
    value: string;
    options?: string[];
    requiredFromInstaller?: boolean;
    hint?: string;
    scope?: FieldScope;
}
interface McpHeaderField {
    key: string;
    value: string;
    requiredFromInstaller: boolean;
    hint?: string;
    label?: string;
    scope?: FieldScope;
}
interface ConfigFieldSchema {
    key: string;
    label: string;
    type: 'text' | 'toggle' | 'select';
    options?: string[];
    requiredFromInstaller: boolean;
    hint?: string;
    scope?: FieldScope;
}
interface InstalledTool {
    id: string;
    installationId: string;
    name: string;
    publisherName: string;
    logo: string;
    type: ToolType;
    category: string;
    description: string;
    longDescription: string;
    rating: number;
    reviewCount: number;
    installs: string;
    version: string;
    installedAt: string;
    isNew: boolean;
    tags: string[];
    features: string[];
    changelog: {
        version: string;
        date: string;
        notes: string[];
    }[];
    reviews: {
        user: string;
        rating: number;
        comment: string;
        date: string;
    }[];
    publisherInfo: {
        name: string;
        verified: boolean;
        totalTools: number;
        bio: string;
    };
    configFields: ConfigField[];
    needsUserCredentials?: boolean;
    installSource?: 'personal' | 'agency';
    skillContent?: string;
    agentShareToken?: string;
    mcpUrl?: string;
    mcpHeaders?: McpHeaderField[];
}
type DetailTab = 'overview' | 'configuration' | 'reviews' | 'changelog' | 'publisher';
function mergeMcpHeaders(rawHeadersList: any, rawSchemaList: any): McpHeaderField[] {
    const headersList: Array<Record<string, string>> = Array.isArray(rawHeadersList) ? rawHeadersList : [];
    const schemaList: ConfigFieldSchema[] = Array.isArray(rawSchemaList) ? rawSchemaList : [];
    const requiredKeys = new Set(schemaList.filter(f => f.requiredFromInstaller).map(f => f.key));
    const hintByKey = new Map(schemaList.map(f => [f.key, f.hint]));
    const labelByKey = new Map(schemaList.map(f => [f.key, f.label]));
    const scopeByKey = new Map(schemaList.map(f => [f.key, f.scope]));
    return headersList.flatMap(header => Object.entries(header).map(([key, value]) => ({
        key,
        value: value ?? '',
        requiredFromInstaller: requiredKeys.has(key),
        hint: hintByKey.get(key) || undefined,
        label: labelByKey.get(key) || undefined,
        scope: scopeByKey.get(key) || 'shared',
    })));
}
function mapApiTool(raw: any): MyTool {
    let kbFileNames: string[] = [];
    if (Array.isArray(raw.kbFileNamesList)) {
        kbFileNames = raw.kbFileNamesList;
    }
    else if (typeof raw.kbFileNames === 'string') {
        try {
            kbFileNames = JSON.parse(raw.kbFileNames);
        }
        catch { }
    }
    else if (Array.isArray(raw.kbFileNames)) {
        kbFileNames = raw.kbFileNames;
    }
    return {
        id: String(raw.id),
        type: INT_TO_TOOL_TYPE[raw.type] ?? 'skill',
        name: raw.name ?? '',
        category: raw.category ?? '',
        icon: raw.icon ?? '',
        description: raw.description ?? '',
        skills: raw.skills ?? '',
        kbFileNames,
        visibility: INT_TO_VISIBILITY[raw.visibility] ?? 'just_me',
        createdAt: raw.createdAt ?? new Date().toISOString(),
        publishStatus: raw.publishStatus ?? undefined,
        publishRequestId: raw.publishRequestId ? String(raw.publishRequestId) : undefined,
        isAgencyInstalled: raw.isAgencyInstalled ?? false,
        mcpUrl: raw.mcpUrl ?? undefined,
        mcpHeaders: mergeMcpHeaders(raw.mcpHeadersTemplateList, raw.configSchemaList),
        skillContent: raw.skillContent ?? undefined,
        agentShareToken: raw.agentShareToken ?? undefined,
    };
}
function mapApiInstalledTool(raw: any): InstalledTool {
    const tool = mapApiTool(raw);
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const schemaList: ConfigFieldSchema[] = Array.isArray(raw.configSchemaList) ? raw.configSchemaList : [];
    return {
        id: tool.id,
        installationId: String(raw.installationId ?? raw.id),
        name: tool.name,
        publisherName: 'My Agency',
        logo: tool.icon || typeInfo.icon,
        type: tool.type,
        category: tool.category,
        description: tool.description,
        longDescription: tool.description,
        rating: 0,
        reviewCount: 0,
        installs: '—',
        version: '1.0.0',
        installedAt: tool.createdAt,
        isNew: false,
        tags: [],
        features: tool.skills
            ? tool.skills.split('\n').map(s => s.trim()).filter(Boolean)
            : [],
        changelog: [],
        reviews: [],
        publisherInfo: { name: 'My Agency', verified: false, totalTools: 0, bio: '' },
        configFields: schemaList.map(f => ({
            key: f.key, label: f.label || f.key, type: f.type, value: '', options: f.options,
            requiredFromInstaller: f.requiredFromInstaller, hint: f.hint, scope: f.scope || 'shared',
        })),
        needsUserCredentials: raw.needsUserCredentials ?? false,
        installSource: raw.installSource ?? 'personal',
        skillContent: tool.skillContent,
        agentShareToken: tool.agentShareToken,
        mcpUrl: tool.mcpUrl,
        mcpHeaders: tool.mcpHeaders,
    };
}
async function apiFetchMyTools(): Promise<MyTool[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/my`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load tools (${res.status})`);
    const json = await res.json();
    const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
    return list.map(mapApiTool);
}
async function apiUploadSkillKbFile(toolId: string, file: File): Promise<{
    id: string;
    filename: string;
}> {
    const formData = new FormData();
    formData.append('formFile', file);
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/kb-files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
        body: formData,
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Failed to upload Knowledge Base file (${res.status})`);
    }
    const json = await res.json();
    return json.data ?? json;
}
async function apiGetSkillKbFiles(toolId: string): Promise<{
    id: string;
    filename: string;
}[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/kb-files`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load Knowledge Base files (${res.status})`);
    const json = await res.json();
    const list: any[] = json.data ?? json;
    return list.map(f => ({ id: String(f.id), filename: f.filename }));
}
async function apiDeleteSkillKbFile(toolId: string, fileId: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/kb-files/${fileId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to remove Knowledge Base file (${res.status})`);
}
async function apiDeleteTool(id: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to delete tool (${res.status})`);
}
async function apiFetchInstalledTools(): Promise<InstalledTool[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/installed`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load installed tools (${res.status})`);
    const json = await res.json();
    const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
    return list.map(mapApiInstalledTool);
}
async function apiUninstallTool(toolId: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/uninstall`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to uninstall tool (${res.status})`);
}
async function apiFetchMarketplaceTools(): Promise<MyTool[]> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/marketplace`, { headers: authHeaders() });
    if (!res.ok)
        throw new Error(`Failed to load marketplace (${res.status})`);
    const json = await res.json();
    const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
    return list.map(mapApiTool);
}
async function apiUpdateInstallationConfig(installationId: string, mcpFieldValues: Record<string, string>): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/installation/${installationId}/config`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ mcpFieldValues }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.success === false) {
        throw new Error((body as {
            message?: string;
        }).message || `Failed to update configuration (${res.status})`);
    }
}
async function apiUninstallToolForAgency(toolId: string): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/uninstall-for-agency`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok)
        throw new Error(`Failed to uninstall for agency (${res.status})`);
}
async function apiUpdateMyCredentials(toolId: string, mcpFieldValues: Record<string, string>): Promise<void> {
    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MyTools/${toolId}/my-credentials`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ mcpFieldValues }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.success === false) {
        throw new Error((body as {
            message?: string;
        }).message || `Failed to save your credentials (${res.status})`);
    }
}
// Read at render, not at module load: these pages are imported when the app first
// loads (usually the login screen), before login writes isAgencyOwner, so a
// module-level read stays false until a full page reload.
const readIsAgencyOwner = () => localStorage.getItem('isAgencyOwner') === 'true';
const TOOL_TYPES: {
    type: ToolType;
    label: string;
    icon: string;
    desc: string;
}[] = [
    { type: 'skill', label: 'Skill', icon: '🧠', desc: 'A reusable AI skill or capability' },
    { type: 'agent', label: 'Agent', icon: '🤖', desc: 'An autonomous AI agent' },
    { type: 'workflow', label: 'Workflow', icon: '🔄', desc: 'An automated workflow or pipeline' },
    { type: 'widget', label: 'Widget', icon: '📊', desc: 'An embeddable UI component' },
    { type: 'mcp_server', label: 'MCP Server', icon: '🖥️', desc: 'Model Context Protocol server' },
    { type: 'integration', label: 'Integration', icon: '🔌', desc: 'Connect to external services' },
];
const CATEGORIES = ['Productivity', 'Insurance', 'Communication', 'Data', 'AI & ML', 'Finance', 'Other'];
const CREDENTIALS_BADGE = { label: '🔑 Needs your credentials', color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' };
const ICON_OPTIONS = [
    '🧠', '🤖', '🔄', '📊', '🖥️', '🔌', '⚡', '🎯',
    '🔍', '📋', '🛠️', '💡', '🔒', '📈', '🗂️', '✨',
    '🚀', '🔗', '📧', '🏦', '🛡️', '📝', '🔔', '🌐',
];
function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
        });
    }
    catch {
        return iso;
    }
}
const StarRating: React.FC<{
    rating: number;
    size?: number;
}> = ({ rating, size = 13 }) => (<span className="mt-stars" style={{ fontSize: size }}>
    {[1, 2, 3, 4, 5].map(n => (<span key={n} style={{ color: n <= Math.round(rating) ? '#f59e0b' : 'rgba(255,255,255,0.15)' }}>★</span>))}
  </span>);
const InstalledToolCard: React.FC<{
    tool: InstalledTool;
    onClick: () => void;
}> = ({ tool, onClick }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    return (<div className="mt-inst-card" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
      <div className="mt-inst-card__top-row">
        <div className="mt-inst-card__logo">{tool.logo}</div>
        <div className="mt-inst-card__top-badges">
          {tool.isNew && <span className="mt-inst-badge mt-inst-badge--new">NEW</span>}
          <span className="mt-inst-badge mt-inst-badge--installed">Installed</span>
        </div>
      </div>
      {tool.needsUserCredentials && (<span style={{
                display: 'inline-block', marginTop: 6, fontSize: 12, fontWeight: 600,
                color: CREDENTIALS_BADGE.color, background: CREDENTIALS_BADGE.bg,
                border: `1px solid ${CREDENTIALS_BADGE.border}`, borderRadius: 6, padding: '2px 8px',
            }}>
          {CREDENTIALS_BADGE.label}
        </span>)}
      <div className="mt-inst-card__name">{tool.name}</div>
      <div className="mt-inst-card__publisher">{tool.publisherName}</div>
      {tool.rating > 0 && (<div className="mt-inst-card__rating-row">
          <StarRating rating={tool.rating}/>
          <span className="mt-inst-card__rating-val">{tool.rating}</span>
          <span className="mt-inst-card__review-count">({tool.reviewCount})</span>
        </div>)}
      <p className="mt-inst-card__desc">{tool.description}</p>
      <div className="mt-inst-card__footer">
        <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
        {tool.installs !== '—' && (<span className="mt-inst-card__installs">{tool.installs} installs</span>)}
      </div>
    </div>);
};
const DETAIL_TABS: {
    key: DetailTab;
    label: string;
}[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'configuration', label: 'Configuration' },
];
const InstalledToolDetail: React.FC<{
    tool: InstalledTool;
    onBack: () => void;
    onUninstall: (id: string, forAgency: boolean) => void;
    uninstalling: boolean;
}> = ({ tool, onBack, onUninstall, uninstalling }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const [activeTab, setActiveTab] = useState<DetailTab>('overview');
    const readOnlySkillEditorRef = useRef<HTMLTextAreaElement>(null);
    const [configValues, setConfigValues] = useState<Record<string, string>>(() => Object.fromEntries(tool.configFields.map(f => [f.key, f.value])));
    const [savingConfig, setSavingConfig] = useState(false);
    const [configSaveMsg, setConfigSaveMsg] = useState<string | null>(null);
    const [confirmUninstall, setConfirmUninstall] = useState(false);
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const detailTabs = tool.type === 'skill' ? DETAIL_TABS.filter(t => t.key !== 'configuration') : DETAIL_TABS;
    const isAgencyInstalled = tool.installSource === 'agency';
    const restrictToPerUser = isAgencyInstalled && !isAgencyOwner;
    const visibleConfigFields = restrictToPerUser
        ? tool.configFields.filter(f => f.scope === 'per_user')
        : tool.configFields;
    const handleSaveConfig = async () => {
        setSavingConfig(true);
        setConfigSaveMsg(null);
        try {
            if (restrictToPerUser) {
                const perUserValues = Object.fromEntries(visibleConfigFields.map(f => [f.key, configValues[f.key]]));
                await apiUpdateMyCredentials(tool.id, perUserValues);
            }
            else {
                await apiUpdateInstallationConfig(tool.installationId, configValues);
            }
            setConfigSaveMsg('Saved.');
        }
        catch (err: any) {
            setConfigSaveMsg(err.message || 'Failed to save configuration.');
        }
        finally {
            setSavingConfig(false);
            setTimeout(() => setConfigSaveMsg(null), 4000);
        }
    };
    return (<motion.div className="mt-inst-detail-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}>

      <button type="button" className="mt-inst-back-btn" onClick={onBack}>
        ← Back to Installed
      </button>

      <div className="mt-inst-detail-header">
        <div className="mt-detail-logo">{tool.logo}</div>
        <div className="mt-detail-header__info">
          <div className="mt-detail-title">{tool.name}</div>
          <div className="mt-detail-publisher-row">
            <span className="mt-detail-publisher">{tool.publisherName}</span>
          </div>
          <div className="mt-detail-meta">
            <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
            <span className="mt-detail-dot">·</span>
            <span className="mt-badge mt-badge--status">Active</span>
            <span className="mt-detail-dot">·</span>
            <span className="mt-detail-dim">Installed {formatDate(tool.installedAt)}</span>
            {tool.needsUserCredentials && (<>
                <span className="mt-detail-dot">·</span>
                <span style={{
                fontSize: 12, fontWeight: 600, color: CREDENTIALS_BADGE.color,
                background: CREDENTIALS_BADGE.bg, border: `1px solid ${CREDENTIALS_BADGE.border}`,
                borderRadius: 6, padding: '2px 8px',
            }}>
                  {CREDENTIALS_BADGE.label}
                </span>
              </>)}
          </div>
        </div>
        <div className="mt-inst-detail-actions">
          <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={() => setConfirmUninstall(true)} disabled={uninstalling || (isAgencyInstalled && !isAgencyOwner)} title={isAgencyInstalled && !isAgencyOwner
            ? 'This tool was installed for your whole agency — ask an Agency Admin to remove it.'
            : undefined}>
            {uninstalling
            ? 'Uninstalling…'
            : isAgencyInstalled && isAgencyOwner ? 'Uninstall for agency' : 'Uninstall'}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {confirmUninstall && (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget)
            setConfirmUninstall(false); }}>
            <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
              <div className="mt-modal__header">
                <div className="mt-modal__header-info">
                  <div className="mt-modal__icon">⚠️</div>
                  <h3 className="mt-modal__title">
                    {isAgencyInstalled && isAgencyOwner
                ? `Uninstall "${tool.name}" for your whole agency?`
                : `Uninstall "${tool.name}"?`}
                  </h3>
                </div>
                <button type="button" className="mt-modal__close" onClick={() => setConfirmUninstall(false)} aria-label="Close">×</button>
              </div>
              <p className="mt-step-subtitle">
                {isAgencyInstalled && isAgencyOwner
                ? 'This removes it for every user in your agency, not just you, and detaches it from each of their agents. This can\'t be undone.'
                : 'This detaches it from your agent and removes your saved credentials. This can\'t be undone.'}
              </p>
              <div className="mt-actions">
                <button type="button" className="mt-btn mt-btn--secondary" onClick={() => setConfirmUninstall(false)}>Cancel</button>
                <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={() => { onUninstall(tool.id, isAgencyInstalled && isAgencyOwner); setConfirmUninstall(false); }}>
                  {isAgencyInstalled && isAgencyOwner ? 'Uninstall for agency' : 'Uninstall'}
                </button>
              </div>
            </motion.div>
          </motion.div>)}
      </AnimatePresence>

      <div className="mt-inst-page-tabs">
        {detailTabs.map(tab => (<button key={tab.key} type="button" className={`mt-inst-page-tab ${activeTab === tab.key ? 'mt-inst-page-tab--active' : ''}`} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={activeTab} className="mt-inst-tab-content" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>

          {activeTab === 'overview' && (<div className="mt-detail-section">
              {tool.longDescription && (<p className="mt-overview-desc">{tool.longDescription}</p>)}
              {tool.tags.length > 0 && (<div className="mt-overview-tags">
                  {tool.tags.map(tag => (<span key={tag} className="mt-badge mt-badge--category">{tag}</span>))}
                </div>)}
              {tool.features.length > 0 && (<>
                  <h4 className="mt-detail-section-title">Skills</h4>
                  <ul className="mt-feature-list">
                    {tool.features.map(f => (<li key={f} className="mt-feature-item">
                        <span className="mt-feature-check">✓</span>{f}
                      </li>))}
                  </ul>
                </>)}
              {tool.type === 'skill' && tool.skillContent && (<>
                  <h4 className="mt-detail-section-title">Skill content</h4>
                  <SkillEditorBody mode="preview" onModeChange={() => { }} body={tool.skillContent} onBodyChange={() => { }} textEditorRef={readOnlySkillEditorRef} insertMd={() => { }} readOnly hideModeTabs/>
                </>)}
              {tool.type === 'agent' && tool.agentShareToken && (<>
                  <h4 className="mt-detail-section-title">Agent bundle</h4>
                  <AgentBundlePreview token={tool.agentShareToken}/>
                </>)}
              {tool.type === 'mcp_server' && (<>
                  <h4 className="mt-detail-section-title">MCP Server</h4>
                  <McpDetailBody url={tool.mcpUrl} headers={tool.mcpHeaders}/>
                </>)}
              {tool.features.length === 0 && !tool.longDescription && !tool.skillContent && !tool.agentShareToken && tool.type !== 'mcp_server' && (<p className="mt-table-empty">No details available.</p>)}
            </div>)}

          {activeTab === 'configuration' && (<div className="mt-detail-section">
              {restrictToPerUser && (<p className="mt-config-hint">
                  This tool was installed for your whole agency by an admin. Shared credentials
                  are already set up — you only need to fill in your own fields below.
                </p>)}
              {visibleConfigFields.length > 0 ? (<>
                  <p className="mt-config-hint">
                    {tool.type === 'mcp_server'
                    ? 'Leave a field blank to keep its current value — only fill in fields you want to change (e.g. after rotating a key).'
                    : 'Changes are saved automatically to your workspace.'}
                  </p>
                  <div className="mt-config-list">
                    {visibleConfigFields.map(field => (<div key={field.key} className="mt-config-field">
                        <label className="mt-config-field__label">
                          {field.label}{field.requiredFromInstaller ? ' *' : ''}
                        </label>
                        {field.hint && <p className="mt-field-hint">{field.hint}</p>}
                        <div className="mt-config-field__control">
                          {field.type === 'toggle' && (<label className="mt-toggle">
                              <input type="checkbox" checked={configValues[field.key] === 'true'} onChange={e => setConfigValues(v => ({ ...v, [field.key]: e.target.checked ? 'true' : 'false' }))}/>
                              <span className="mt-toggle__track"/>
                            </label>)}
                          {field.type === 'select' && (<select className="mt-select" style={{ width: 220 }} value={configValues[field.key]} onChange={e => setConfigValues(v => ({ ...v, [field.key]: e.target.value }))}>
                              {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>)}
                          {field.type === 'text' && (<input type="text" className="mt-input" style={{ width: 220 }} value={configValues[field.key]} onChange={e => setConfigValues(v => ({ ...v, [field.key]: e.target.value }))}/>)}
                        </div>
                      </div>))}
                  </div>
                  {tool.type === 'mcp_server' && (<div className="mt-actions" style={{ marginTop: 12 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{configSaveMsg}</span>
                      <button type="button" className="mt-btn mt-btn--primary" onClick={handleSaveConfig} disabled={savingConfig}>
                        {savingConfig ? 'Saving…' : 'Save'}
                      </button>
                    </div>)}
                </>) : (<p className="mt-table-empty">No configuration options available for this tool.</p>)}
            </div>)}

        </motion.div>
      </AnimatePresence>
    </motion.div>);
};
const StepIndicator: React.FC<{
    current: number;
}> = ({ current }) => {
    const steps = ['Type', 'Info', 'Review'];
    return (<div className="mt-steps">
      {steps.map((label, idx) => {
            const stepNum = idx + 1;
            const isCompleted = stepNum < current;
            const isActive = stepNum === current;
            return (<React.Fragment key={label}>
            <div className={`mt-step ${isActive ? 'mt-step--active' : ''} ${isCompleted ? 'mt-step--done' : ''}`}>
              <div className="mt-step__circle">{isCompleted ? '✓' : stepNum}</div>
              <span className="mt-step__label">{label}</span>
            </div>
            {idx < steps.length - 1 && (<div className={`mt-step-line ${isCompleted ? 'mt-step-line--done' : ''}`}/>)}
          </React.Fragment>);
        })}
    </div>);
};
const Step1Type: React.FC<{
    selected: ToolType | null;
    onSelect: (t: ToolType) => void;
    onNext: () => void;
}> = ({ selected, onSelect, onNext }) => (<motion.div key="step1" className="mt-step-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
    <h2 className="mt-step-title">Choose what to publish</h2>
    <p className="mt-step-subtitle">Select the type of tool you want to create.</p>
    <div className="mt-type-grid">
      {TOOL_TYPES.map(t => {
        const enabled = t.type === 'skill' || t.type === 'mcp_server' || t.type === 'agent';
        return (<button key={t.type} type="button" className={`mt-type-card ${selected === t.type ? 'mt-type-card--selected' : ''} ${!enabled ? 'mt-type-card--disabled' : ''}`} onClick={() => enabled && onSelect(t.type)} disabled={!enabled}>
            {!enabled && <span className="mt-type-card__coming-soon">Coming Soon</span>}
            <span className="mt-type-card__icon">{t.icon}</span>
            <span className="mt-type-card__label">{t.label}</span>
            <span className="mt-type-card__desc">{t.desc}</span>
          </button>);
    })}
    </div>
    <div className="mt-actions">
      <span />
      <button type="button" className="mt-btn mt-btn--primary" disabled={!selected} onClick={onNext}>
        Next →
      </button>
    </div>
  </motion.div>);
const Step2Info: React.FC<{
    selectedType: ToolType;
    name: string;
    setName: (v: string) => void;
    category: string;
    setCategory: (v: string) => void;
    icon: string;
    setIcon: (v: string) => void;
    description: string;
    setDescription: (v: string) => void;
    skills: string;
    setSkills: (v: string) => void;
    skillContent: string;
    setSkillContent: (v: string) => void;
    kbFiles: File[];
    setKbFiles: (v: File[]) => void;
    editingId: string | null;
    existingSkillKbFiles: {
        id: string;
        filename: string;
    }[];
    setExistingSkillKbFiles: (v: {
        id: string;
        filename: string;
    }[]) => void;
    visibility: Visibility;
    setVisibility: (v: Visibility) => void;
    mcpUrl: string;
    setMcpUrl: (v: string) => void;
    mcpHeaders: McpHeaderField[];
    setMcpHeaders: (v: McpHeaderField[]) => void;
    agentId: string;
    setAgentId: (v: string) => void;
    agentShareToken: string;
    setAgentShareToken: (v: string) => void;
    agentPreview: AgentShareSnapshot | null;
    setAgentPreview: (v: AgentShareSnapshot | null) => void;
    agentFrozenSnapshot: AgentShareSnapshot | null;
    setAgentFrozenSnapshot: (v: AgentShareSnapshot | null) => void;
    onBack: () => void;
    onNext: () => void;
}> = ({ selectedType, name, setName, category, setCategory, icon, setIcon, description, setDescription, skills, setSkills, skillContent, setSkillContent, kbFiles, setKbFiles, editingId, existingSkillKbFiles, setExistingSkillKbFiles, visibility, setVisibility, mcpUrl, setMcpUrl, mcpHeaders, setMcpHeaders, agentId, setAgentId, agentShareToken, setAgentShareToken, agentPreview, setAgentPreview, agentFrozenSnapshot, setAgentFrozenSnapshot, onBack, onNext, }) => {
    const [dwAgents, setDwAgents] = useState<DwAgentRecord[]>([]);
    const [dwAgentsLoading, setDwAgentsLoading] = useState(false);
    const [agentPreviewLoading, setAgentPreviewLoading] = useState(false);
    const [agentPreviewError, setAgentPreviewError] = useState('');
    const [agentUpdating, setAgentUpdating] = useState(false);
    useEffect(() => {
        if (selectedType !== 'agent')
            return;
        setDwAgentsLoading(true);
        listDwAgents().then(list => setDwAgents(list.filter(a => !a.is_system))).catch(() => setDwAgents([])).finally(() => setDwAgentsLoading(false));
    }, [selectedType]);
    const mintAgentShare = async (id: string, snap: AgentShareSnapshot | null) => {
        const share = await shareAgent(id, 'anyone');
        setAgentShareToken(share.token);
        setAgentFrozenSnapshot(snap);
    };
    useEffect(() => {
        if (selectedType !== 'agent' || !agentId) {
            setAgentPreview(null);
            return;
        }
        let cancelled = false;
        setAgentPreviewLoading(true);
        setAgentPreviewError('');
        previewAgentShare(agentId)
            .then(async ({ snapshot }) => {
            if (cancelled)
                return;
            setAgentPreview(snapshot);
            setName(snapshot.name || '');
            setDescription(snapshot.description || '');
            if (!editingId) {
                setAgentUpdating(true);
                try {
                    await mintAgentShare(agentId, snapshot);
                }
                catch (err: any) {
                    if (!cancelled)
                        setAgentPreviewError(err.message || 'Failed to sync this agent');
                }
                finally {
                    if (!cancelled)
                        setAgentUpdating(false);
                }
            }
        })
            .catch((err: any) => { if (!cancelled) {
            setAgentPreview(null);
            setAgentPreviewError(err.message || 'Failed to load this agent');
        } })
            .finally(() => { if (!cancelled)
            setAgentPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [selectedType, agentId, editingId]);
    const snapshotsMatch = (a: AgentShareSnapshot | null, b: AgentShareSnapshot | null) => {
        if (!a || !b)
            return false;
        return JSON.stringify({ system: a.system, model: a.model, description: a.description, skills: a.skills, mcp_servers: a.mcp_servers, user_rules: a.user_rules })
            === JSON.stringify({ system: b.system, model: b.model, description: b.description, skills: b.skills, mcp_servers: b.mcp_servers, user_rules: b.user_rules });
    };
    const upToDate = !!agentShareToken && snapshotsMatch(agentPreview, agentFrozenSnapshot);
    const handleUpdateAgentShare = async () => {
        if (!agentId)
            return;
        setAgentUpdating(true);
        try {
            await mintAgentShare(agentId, agentPreview);
        }
        catch (err: any) {
            alert(err.message || 'Failed to sync this agent');
        }
        finally {
            setAgentUpdating(false);
        }
    };
    const [skillPreviewMode, setSkillPreviewMode] = useState<'edit' | 'preview'>('edit');
    const skillTextEditorRef = useRef<HTMLTextAreaElement>(null);
    const insertSkillMd = useCallback((before: string, after: string, linePrefix = '') => {
        const el = skillTextEditorRef.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = linePrefix
            ? `${linePrefix}${selected || 'text'}`
            : `${before}${selected || 'text'}${after}`;
        setSkillContent(value.slice(0, s) + replacement + value.slice(e));
        setTimeout(() => {
            const newPos = s + replacement.length;
            el.setSelectionRange(newPos, newPos);
            el.focus();
        }, 0);
    }, [setSkillContent]);
    const [deletingKbFileId, setDeletingKbFileId] = useState<string | null>(null);
    const removeExistingKbFile = async (fileId: string) => {
        if (!editingId)
            return;
        setDeletingKbFileId(fileId);
        try {
            await apiDeleteSkillKbFile(editingId, fileId);
            setExistingSkillKbFiles(existingSkillKbFiles.filter(f => f.id !== fileId));
        }
        catch (err: any) {
            alert(err.message || 'Failed to remove Knowledge Base file');
        }
        finally {
            setDeletingKbFileId(null);
        }
    };
    const [kbDragOver, setKbDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const typeInfo = TOOL_TYPES.find(t => t.type === selectedType)!;
    const displayIcon = icon.trim() || typeInfo.icon;
    // Mirror what the upsert endpoint actually requires, so the wizard cannot
    // reach Review and Save in a state the backend will reject: a name always,
    // skill content for a skill, an MCP URL for an MCP server, a resolved share
    // token for an agent. Previously only the name and the agent token were
    // checked, so a skill with an empty body sailed through to Save and came
    // back as a bare 400.
    const missingFieldHint = !name.trim()
        ? 'Give the tool a name to continue.'
        : selectedType === 'skill' && !skillContent.trim()
            ? 'Add the skill content to continue.'
            : selectedType === 'mcp_server' && !mcpUrl.trim()
                ? 'Add the MCP server URL to continue.'
                : selectedType === 'agent' && !agentShareToken
                    ? 'Select an agent to continue.'
                    : '';
    const canContinue = !missingFieldHint;
    const KB_ALLOWED_EXTENSIONS = ['.pdf', '.txt'];
    const KB_MAX_BYTES = 10 * 1024 * 1024;
    const [kbError, setKbError] = useState('');
    const addFiles = (incoming: FileList | null) => {
        if (!incoming)
            return;
        setKbError('');
        const accepted: File[] = [];
        for (const f of Array.from(incoming)) {
            if (kbFiles.some(ex => ex.name === f.name))
                continue;
            const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
            if (!KB_ALLOWED_EXTENSIONS.includes(ext)) {
                setKbError(`"${f.name}" isn't a supported type — only PDF and TXT files are accepted.`);
                continue;
            }
            if (f.size > KB_MAX_BYTES) {
                setKbError(`"${f.name}" is larger than the 10MB limit.`);
                continue;
            }
            accepted.push(f);
        }
        if (accepted.length)
            setKbFiles([...kbFiles, ...accepted]);
    };
    const addHeaderRow = () => setMcpHeaders([...mcpHeaders, { key: '', value: '', requiredFromInstaller: false }]);
    const updateHeaderRow = (idx: number, changes: Partial<McpHeaderField>) => setMcpHeaders(mcpHeaders.map((h, i) => i === idx ? { ...h, ...changes } : h));
    const removeHeaderRow = (idx: number) => setMcpHeaders(mcpHeaders.filter((_, i) => i !== idx));
    const renderScopeToggle = (idx: number, currentScope?: FieldScope) => (<div className="mt-visibility-group" style={{ marginTop: 6 }}>
      {([
            ['shared', 'One value for the whole agency'],
            ['per_user', 'Each person enters their own'],
        ] as const).map(([val, label]) => (<button key={val} type="button" className={`mt-visibility-btn ${(currentScope ?? 'shared') === val ? 'mt-visibility-btn--active' : ''}`} onClick={() => updateHeaderRow(idx, { scope: val })}>
          {label}
        </button>))}
    </div>);
    const isApiKeyShape = (headers: McpHeaderField[]) => headers.length === 1 && headers[0].key === 'Authorization' && headers[0].requiredFromInstaller;
    const [authMode, setAuthMode] = useState<'none' | 'apiKey' | 'advanced'>(() => {
        if (mcpHeaders.length === 0)
            return 'none';
        return isApiKeyShape(mcpHeaders) ? 'apiKey' : 'advanced';
    });
    const selectAuthMode = (mode: 'none' | 'apiKey' | 'advanced') => {
        setAuthMode(mode);
        if (mode === 'none') {
            setMcpHeaders([]);
        }
        else if (mode === 'apiKey' && !isApiKeyShape(mcpHeaders)) {
            setMcpHeaders([{ key: 'Authorization', value: '', requiredFromInstaller: true, label: 'API Key', hint: '' }]);
        }
        else if (mode === 'advanced' && mcpHeaders.length === 0) {
            setMcpHeaders([{ key: '', value: '', requiredFromInstaller: false }]);
        }
    };
    return (<motion.div key="step2" className="mt-step-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">Tool details</h2>
      <p className="mt-step-subtitle">Describe your {typeInfo.label.toLowerCase()} so others know what it does.</p>

      <div className="mt-form">
        {selectedType === 'agent' && (<>
            <div className="mt-field mt-field--full">
              <label className="mt-label">Digital Workforce agent <span className="mt-required">*</span></label>
              <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
                {editingId
                ? 'This listing wraps one of your existing agents — its agents.md, skills, and MCPs are pulled straight from Digital Workforce, not authored here. Edit the agent itself in Digital Workforce, then come back and click Update to pull in the latest.'
                : 'This listing wraps one of your existing agents — its agents.md, skills, and MCPs are pulled straight from Digital Workforce, not authored here.'}
              </p>
              <div className="mt-field-row" style={{ alignItems: 'flex-start' }}>
                {dwAgentsLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12.5 }}>Loading your agents…</p>) : (<select className="mt-select" style={{ flex: 1 }} value={agentId} onChange={e => setAgentId(e.target.value)}>
                    <option value="">Select an agent…</option>
                    {dwAgents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
                  </select>)}
                {editingId ? (<button type="button" className="mt-btn mt-btn--secondary" disabled={!agentId || agentPreviewLoading || agentUpdating || upToDate} onClick={handleUpdateAgentShare}>
                    {agentUpdating ? 'Updating…' : upToDate ? 'Up to date' : 'Update'}
                  </button>) : agentUpdating ? (<span style={{ fontSize: 12.5, color: 'var(--text-tertiary)', alignSelf: 'center' }}>Syncing…</span>) : null}
              </div>
              {agentPreviewError && <p className="mt-error-text" style={{ marginTop: 6 }}>{agentPreviewError}</p>}
            </div>

            {agentId && (<div className="mt-field mt-field--full">
                <label className="mt-label">Preview</label>
                {agentPreviewLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12.5 }}>Loading agent…</p>) : agentPreview ? (<AgentDraftPreview system={agentPreview.system ?? ''} skills={agentPreview.skills} mcpServers={agentPreview.mcp_servers} userRules={agentPreview.user_rules}/>) : null}
              </div>)}
          </>)}

        <div className="mt-field-row">
          <div className="mt-field mt-field--grow">
            <label className="mt-label">Name <span className="mt-required">*</span></label>
            <input className="mt-input" type="text" placeholder={`My ${typeInfo.label}`} value={name} onChange={e => setName(e.target.value)} disabled={selectedType === 'agent'}/>
            {selectedType === 'agent' && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>Matches the selected agent's name.</p>)}
          </div>
          <div className="mt-field">
            <label className="mt-label">Category</label>
            <select className="mt-select" value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-field mt-field--full">
          <label className="mt-label">Icon</label>
          <div className="mt-icon-picker">
            {ICON_OPTIONS.map(emoji => (<button key={emoji} type="button" className={`mt-icon-option ${displayIcon === emoji ? 'mt-icon-option--selected' : ''}`} onClick={() => setIcon(emoji)} title={emoji}>
                {emoji}
              </button>))}
          </div>
        </div>

        <div className="mt-field mt-field--full">
          <label className="mt-label">Description</label>
          <textarea className="mt-textarea" placeholder="What does this tool do? Who is it for?" rows={5} value={description} onChange={e => setDescription(e.target.value)} disabled={selectedType === 'agent'}/>
          {selectedType === 'agent' && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>Matches the selected agent's description.</p>)}
        </div>

        {selectedType === 'mcp_server' ? (<>
            <div className="mt-field mt-field--full">
              <label className="mt-label">Server URL <span className="mt-required">*</span></label>
              <input className="mt-input" type="text" placeholder="https://example.com/mcp" value={mcpUrl} onChange={e => setMcpUrl(e.target.value)}/>
            </div>

            <div className="mt-field mt-field--full">
              <label className="mt-label">Authentication</label>
              <div className="mt-visibility-group">
                {([
                ['none', 'No key needed'],
                ['apiKey', 'Requires an API key'],
                ['advanced', 'Advanced'],
            ] as const).map(([val, label]) => (<button key={val} type="button" className={`mt-visibility-btn ${authMode === val ? 'mt-visibility-btn--active' : ''}`} onClick={() => selectAuthMode(val)}>
                    {label}
                  </button>))}
              </div>

              {authMode === 'apiKey' && (<div style={{ marginTop: 10 }}>
                  <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
                    {(mcpHeaders[0]?.scope ?? 'shared') === 'shared'
                    ? 'An Agency Admin enters this once when installing for the whole agency — everyone uses that same value automatically. A personal (non-agency) install still prompts the installer directly.'
                    : "Each person supplies their own — at install time for a personal install, or afterward for an agency install (until they do, they'll see a \"needs your credentials\" prompt)."}
                  </p>
                  {renderScopeToggle(0, mcpHeaders[0]?.scope)}
                  <label className="mt-label" style={{ marginTop: 8 }}>Field label</label>
                  <input className="mt-input" type="text" placeholder="API Key" value={mcpHeaders[0]?.label ?? ''} onChange={e => updateHeaderRow(0, { label: e.target.value })}/>
                  <label className="mt-label" style={{ marginTop: 8 }}>Instructions for installer</label>
                  <input className="mt-input" type="text" placeholder='e.g. "Find yours at firecrawl.dev/app/api-keys"' value={mcpHeaders[0]?.hint ?? ''} onChange={e => updateHeaderRow(0, { hint: e.target.value })}/>
                </div>)}

              {authMode === 'advanced' && (<div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span className="mt-step-subtitle" style={{ margin: 0 }}>Custom HTTP headers</span>
                    <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={addHeaderRow}>
                      + Add header
                    </button>
                  </div>
                  <p className="mt-step-subtitle" style={{ margin: '4px 0 8px' }}>
                    Check "Installer supplies" for any header whose value should never be stored here.
                  </p>
                  {mcpHeaders.map((header, idx) => (<div key={idx} className="mt-header-row-group" style={{ marginBottom: 8 }}>
                      <div className="mt-field-row" style={{ alignItems: 'center' }}>
                        <input className="mt-input" type="text" placeholder="Header name" style={{ flex: 1 }} value={header.key} onChange={e => updateHeaderRow(idx, { key: e.target.value })}/>
                        <input className="mt-input" type="text" style={{ flex: 1 }} placeholder={header.requiredFromInstaller ? 'Supplied by installer' : 'Value'} value={header.value} disabled={header.requiredFromInstaller} onChange={e => updateHeaderRow(idx, { value: e.target.value })}/>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={header.requiredFromInstaller} onChange={e => updateHeaderRow(idx, { requiredFromInstaller: e.target.checked, value: e.target.checked ? '' : header.value })}/>
                          Installer supplies
                        </label>
                        <button type="button" className="mt-kb-file-item__remove" onClick={() => removeHeaderRow(idx)} aria-label="Remove header">×</button>
                      </div>
                      {header.requiredFromInstaller && (<>
                          {renderScopeToggle(idx, header.scope)}
                          <input className="mt-input" type="text" style={{ marginTop: 6 }} placeholder='Field label shown to the installer, e.g. "API Key"' value={header.label ?? ''} onChange={e => updateHeaderRow(idx, { label: e.target.value })}/>
                          <input className="mt-input" type="text" style={{ marginTop: 6 }} placeholder='Instructions for the installer, e.g. "Bearer <your API key> — find it under Settings → API Keys"' value={header.hint ?? ''} onChange={e => updateHeaderRow(idx, { hint: e.target.value })}/>
                        </>)}
                    </div>))}
                </div>)}
            </div>
          </>) : selectedType === 'skill' ? (<div className="mt-field mt-field--full">
            <label className="mt-label">Skill content <span className="mt-required">*</span></label>
            <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
              Written as a SKILL.md file — describe what the agent should do and how.
            </p>
            <SkillEditorBody mode={skillPreviewMode} onModeChange={setSkillPreviewMode} body={skillContent} onBodyChange={setSkillContent} textEditorRef={skillTextEditorRef} insertMd={insertSkillMd}/>
          </div>) : selectedType === 'agent' ? null : (<div className="mt-field mt-field--full">
            <label className="mt-label">Skills</label>
            <textarea className="mt-textarea" placeholder="List the skills this tool uses or provides, one per line…" rows={5} value={skills} onChange={e => setSkills(e.target.value)}/>
          </div>)}

        {selectedType !== 'mcp_server' && selectedType !== 'agent' && (<div className="mt-field mt-field--full">
            <label className="mt-label">Knowledge Base</label>
            <div className={`mt-kb-dropzone ${kbDragOver ? 'mt-kb-dropzone--over' : ''}`} onDragOver={e => { e.preventDefault(); setKbDragOver(true); }} onDragLeave={() => setKbDragOver(false)} onDrop={e => { e.preventDefault(); setKbDragOver(false); addFiles(e.dataTransfer.files); }}>
              <span className="mt-kb-dropzone__icon">📂</span>
              <span className="mt-kb-dropzone__text">Drag & drop files here</span>
              <span className="mt-kb-dropzone__or">or</span>
              <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => fileInputRef.current?.click()}>
                Browse Files
              </button>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt" style={{ display: 'none' }} onChange={e => addFiles(e.target.files)}/>
              <span className="mt-kb-dropzone__hint" style={{ fontSize: 11, opacity: 0.7 }}>PDF, TXT (Max 10 MB)</span>
            </div>
            {kbError && (<div className="mt-error-text" style={{ marginTop: 6 }}>{kbError}</div>)}
            {selectedType === 'skill' && existingSkillKbFiles.length > 0 && (<div className="mt-kb-file-list">
                {existingSkillKbFiles.map(f => (<div key={f.id} className="mt-kb-file-item">
                    <span className="mt-kb-file-item__icon">📄</span>
                    <span className="mt-kb-file-item__name">{f.filename}</span>
                    <button type="button" className="mt-kb-file-item__remove" disabled={deletingKbFileId === f.id} onClick={() => removeExistingKbFile(f.id)} aria-label={`Remove ${f.filename}`}>
                      {deletingKbFileId === f.id ? '…' : '×'}
                    </button>
                  </div>))}
              </div>)}
            {kbFiles.length > 0 && (<div className="mt-kb-file-list">
                {kbFiles.map((f, idx) => (<div key={f.name + idx} className="mt-kb-file-item">
                    <span className="mt-kb-file-item__icon">📄</span>
                    <span className="mt-kb-file-item__name">{f.name}</span>
                    <span className="mt-kb-file-item__size">{(f.size / 1024).toFixed(1)} KB</span>
                    <button type="button" className="mt-kb-file-item__remove" onClick={() => setKbFiles(kbFiles.filter((_, i) => i !== idx))} aria-label={`Remove ${f.name}`}>×</button>
                  </div>))}
              </div>)}
          </div>)}

        <div className="mt-field mt-field--full">
          <label className="mt-label">Visibility</label>
          <div className="mt-visibility-group">
            {([['just_me', '📝 Personal'], ['my_agency', '🏢 My Agency'], ['published', '🌐 Global']] as const).map(([val, label]) => (<button key={val} type="button" className={`mt-visibility-btn ${visibility === val ? 'mt-visibility-btn--active' : ''}`} onClick={() => setVisibility(val)}>
                  {label}
                </button>))}
          </div>
          {visibility === 'published' && (<p className="mt-step-subtitle" style={{ margin: '8px 0 0', color: '#fbbf24' }}>
              ⚠ Global tools are visible to every agency on the platform, so this doesn't publish
              immediately — saving submits it for a platform admin's review. It stays private to
              you until approved.
            </p>)}
        </div>
      </div>

      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack}>← Back</button>
        <button type="button" className="mt-btn mt-btn--primary" disabled={!canContinue} onClick={onNext}>
          Next →
        </button>
        {!canContinue && missingFieldHint && (<span className="mt-field-hint">{missingFieldHint}</span>)}
      </div>
    </motion.div>);
};
const Step3Review: React.FC<{
    tool: Omit<MyTool, 'id' | 'createdAt'>;
    agentPreview?: AgentShareSnapshot | null;
    onBack: () => void;
    onSave: () => void;
    isEditing?: boolean;
    saving?: boolean;
}> = ({ tool, agentPreview, onBack, onSave, isEditing, saving }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || typeInfo.icon;
    const visibilityLabel = tool.visibility === 'just_me' ? 'Personal' : tool.visibility === 'my_agency' ? 'My Agency' : 'Published';
    return (<motion.div key="step3" className="mt-step-content" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">Review & publish</h2>
      <p className="mt-step-subtitle">Confirm the details before saving your tool.</p>

      <div className="mt-review-card">
        <div className="mt-review-card__header">
          <div className="mt-review-card__icon-circle">{displayIcon}</div>
          <div>
            <div className="mt-review-card__name">{tool.name}</div>
            <div className="mt-review-card__meta">{isEditing ? 'Editing existing tool' : 'New tool'}</div>
          </div>
        </div>
        <div className="mt-review-card__badges">
          <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
          <span className="mt-badge mt-badge--category">{tool.category}</span>
          <span className="mt-badge mt-badge--visibility">{visibilityLabel}</span>
        </div>
        {tool.description && <p className="mt-review-card__description">{tool.description}</p>}
        {tool.skills.trim() && (<div className="mt-review-card__skills">
            <span className="mt-label" style={{ fontSize: 11 }}>Skills</span>
            <p className="mt-review-card__description" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{tool.skills.trim()}</p>
          </div>)}
        {tool.kbFileNames.length > 0 && (<div className="mt-review-card__skills" style={{ marginTop: 10 }}>
            <span className="mt-label" style={{ fontSize: 11 }}>Knowledge Base</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {tool.kbFileNames.map(n => (<span key={n} className="mt-review-skill-chip">📄 {n}</span>))}
            </div>
          </div>)}
        {tool.type === 'agent' && agentPreview && (<div className="mt-review-card__skills" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="mt-label" style={{ fontSize: 11 }}>Agent bundle</span>
            <AgentDraftPreview system={agentPreview.system ?? ''} skills={agentPreview.skills} mcpServers={agentPreview.mcp_servers} userRules={agentPreview.user_rules}/>
          </div>)}
      </div>

      {!isEditing && tool.type !== 'agent' && (<p className="mt-step-subtitle" style={{ margin: '4px 0 0' }}>Saving installs this for you automatically.</p>)}

      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack} disabled={saving}>
          ← Back
        </button>
        <button type="button" className="mt-btn mt-btn--primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : isEditing ? 'Update' : 'Save'}
        </button>
      </div>
    </motion.div>);
};
const InstalledView: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tools, setTools] = useState<InstalledTool[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [uninstallingId, setUninstallingId] = useState<string | null>(null);
    useEffect(() => {
        setLoading(true);
        setError(null);
        apiFetchInstalledTools()
            .then(setTools)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);
    const toolId = searchParams.get('toolId');
    const selectedTool = toolId ? (tools.find(t => t.id === toolId) ?? null) : null;
    const handleUninstall = useCallback(async (id: string, forAgency: boolean) => {
        setUninstallingId(id);
        try {
            if (forAgency) {
                await apiUninstallToolForAgency(id);
            }
            else {
                await apiUninstallTool(id);
            }
            setTools(prev => prev.filter(t => t.id !== id));
            setSearchParams({ view: 'installed' });
        }
        catch (err: any) {
            alert(err.message || 'Failed to uninstall tool');
        }
        finally {
            setUninstallingId(null);
        }
    }, [setSearchParams]);
    if (loading) {
        return (<div className="mt-installed-grid">
        {Array.from({ length: 6 }).map((_, i) => (<SkeletonLoader key={i} height={180} lines={3}/>))}
      </div>);
    }
    if (error) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">⚠️</div>
        <div className="mt-empty-state__title">Failed to load</div>
        <div className="mt-empty-state__subtitle">{error}</div>
      </div>);
    }
    if (selectedTool) {
        return (<AnimatePresence mode="wait">
        <InstalledToolDetail key={selectedTool.id} tool={selectedTool} onBack={() => setSearchParams({ view: 'installed' })} onUninstall={handleUninstall} uninstalling={uninstallingId === selectedTool.id}/>
      </AnimatePresence>);
    }
    if (tools.length === 0) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">📦</div>
        <div className="mt-empty-state__title">No tools installed</div>
        <div className="mt-empty-state__subtitle">Tools you install will appear here</div>
      </div>);
    }
    return (<div className="mt-installed-grid">
      {tools.map(tool => (<InstalledToolCard key={tool.id} tool={tool} onClick={() => setSearchParams({ view: 'installed', toolId: tool.id })}/>))}
    </div>);
};
const MarketplaceInstallModal: React.FC<{
    tool: InstallableTool;
    mode: 'personal' | 'agency';
    onCancel: () => void;
    onConfirm: (fieldValues: Record<string, string>) => void;
    installing: boolean;
}> = ({ tool, mode, onCancel, onConfirm, installing }) => {
    const requiredFields = (tool.mcpHeaders ?? []).filter(h => h.requiredFromInstaller && (mode === 'personal' || h.scope !== 'per_user'));
    const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(requiredFields.map(f => [f.key, ''])));
    const requiresTrust = tool.visibility === 'published';
    const [trusted, setTrusted] = useState(false);
    const canSubmit = requiredFields.every(f => values[f.key]?.trim()) && (!requiresTrust || trusted);
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || typeInfo.icon;
    return (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget && !installing)
        onCancel(); }}>
      <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
        <div className="mt-modal__header">
          <div className="mt-modal__header-info">
            <div className="mt-modal__icon">{displayIcon}</div>
            <h3 className="mt-modal__title">{mode === 'agency' ? `Install ${tool.name} for your agency` : `Install ${tool.name}`}</h3>
          </div>
          <button type="button" className="mt-modal__close" onClick={onCancel} disabled={installing} aria-label="Close">×</button>
        </div>
        {tool.description && (<p className="mt-step-subtitle" style={{ marginBottom: 4 }}>{tool.description}</p>)}
        {tool.type === 'mcp_server' && (<McpDetailBody url={tool.mcpUrl} headers={tool.mcpHeaders}/>)}
        {requiredFields.length > 0 && (<p className="mt-step-subtitle">
            {mode === 'agency'
              ? 'These shared values apply to everyone in your agency. Anyone with a per-user field will be asked to fill in their own after install.'
              : 'This tool needs a few values from you before it can connect.'}
          </p>)}
        <div className="mt-form">
          {requiredFields.map(f => (<div key={f.key} className="mt-field mt-field--full">
              <label className="mt-label">{f.label || f.key} <span className="mt-required">*</span></label>
              {f.hint && <p className="mt-field-hint">{f.hint}</p>}
              <input className="mt-input" type="text" value={values[f.key] ?? ''} onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}/>
            </div>))}
          {requiresTrust && (<label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={trusted} onChange={e => setTrusted(e.target.checked)} style={{ marginTop: 2 }}/>
              I trust this community shared item and understand it comes from another agency in the Momentum AMP community, not from Momentum AMP directly.
            </label>)}
        </div>
        <div className="mt-actions">
          <button type="button" className="mt-btn mt-btn--secondary" onClick={onCancel} disabled={installing}>Cancel</button>
          <button type="button" className="mt-btn mt-btn--primary" disabled={!canSubmit || installing} onClick={() => onConfirm(values)}>
            {installing ? 'Installing…' : 'Install'}
          </button>
        </div>
      </motion.div>
    </motion.div>);
};
const MarketplaceCard: React.FC<{
    tool: MyTool;
    onViewDetail: () => void;
    onInstall: () => void;
    onInstallForAgency: () => void;
    installing: boolean;
    installed: boolean;
}> = ({ tool, onViewDetail, onInstall, onInstallForAgency, installing, installed }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || typeInfo.icon;
    return (<div className="mt-inst-card" onClick={onViewDetail} style={{ cursor: 'pointer' }}>
      <div className="mt-inst-card__top-row">
        <div className="mt-inst-card__logo">{displayIcon}</div>
      </div>
      <div className="mt-inst-card__name">{tool.name}</div>
      <p className="mt-inst-card__desc">{tool.description}</p>
      <div className="mt-inst-card__footer">
        <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
        <span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span>
      </div>
      <div className="mt-actions" style={{ marginTop: 10, gap: 8 }}>
        <span />
        {isAgencyOwner && (<button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={e => { e.stopPropagation(); onInstallForAgency(); }} disabled={installing || installed} title="Install once for every user in your agency">
            {installing ? 'Installing…' : 'Install for agency'}
          </button>)}
        <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" onClick={e => { e.stopPropagation(); onInstall(); }} disabled={installing || installed} title={installed ? "You've already installed this — manage it from the Installed tab" : undefined}>
          {installed ? 'Installed' : installing ? 'Installing…' : 'Install'}
        </button>
      </div>
    </div>);
};
// Look-before-you-install detail view for a Browse tab listing — was previously
// nothing at all: clicking a card either installed immediately or (for an Agent
// listing) jumped straight into the import flow, with no way to see what's
// actually in it first. Reuses AgentBundlePreview's teaser mode (name +
// description per skill/MCP, never full skill content or a real MCP URL) —
// same as the Agent-share resolve page and My Creations' own detail panel.
// Read-only detail page for a Browse listing — was previously a popup
// (MarketplaceDetailModal), which Sanket flagged as too cramped for anything
// beyond a couple of fields. Shaped like InstalledToolDetail (full page,
// mt-inst-detail-page / mt-inst-back-btn) rather than a modal, and driven by
// the URL the same way InstalledView drives its own detail
// (?view=marketplace&toolId=<id>) so a refresh or a shared link still resolves
// it, once the tool list itself has loaded.
//
// This is Browse, not an editor: there is nothing here for the read-only
// convention (DigitalWorkforcePage's is_system-driven field locking) to gate,
// since a listing you're browsing was never yours to edit in the first
// place — the only actions are Install / Install for agency.
const MarketplaceToolDetail: React.FC<{
    tool: MyTool;
    isAgencyOwner: boolean;
    onBack: () => void;
    onInstall: () => void;
    onInstallForAgency: () => void;
    installing: boolean;
    installed: boolean;
}> = ({ tool, isAgencyOwner, onBack, onInstall, onInstallForAgency, installing, installed }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || typeInfo.icon;
    return (<motion.div className="mt-inst-detail-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}>
      <button type="button" className="mt-inst-back-btn" onClick={onBack}>
        ← Back to Browse
      </button>

      <div className="mt-inst-detail-header">
        <div className="mt-detail-logo">{displayIcon}</div>
        <div className="mt-detail-header__info">
          <div className="mt-detail-title">{tool.name}</div>
          <div className="mt-detail-meta">
            <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
            <span className="mt-detail-dot">·</span>
            <span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span>
          </div>
        </div>
        <div className="mt-inst-detail-actions">
          {isAgencyOwner && (<button type="button" className="mt-btn mt-btn--secondary" onClick={onInstallForAgency} disabled={installing || installed}>
              {installing ? 'Installing…' : 'Install for agency'}
            </button>)}
          <button type="button" className="mt-btn mt-btn--primary" onClick={onInstall} disabled={installing || installed}>
            {installed ? 'Installed' : installing ? 'Installing…' : 'Install'}
          </button>
        </div>
      </div>

      <div className="mt-detail-section">
        {tool.description && (<p className="mt-overview-desc">{tool.description}</p>)}
        {/* showSkillContent=false: this is the marketplace browse-to-install
            surface — the one context skill.md must stay hidden in, per
            AgentBundleItemModal's comment. */}
        {tool.type === 'agent' && tool.agentShareToken && (<AgentBundlePreview token={tool.agentShareToken} showSkillContent={false}/>)}
        {tool.type === 'mcp_server' && (<McpDetailBody url={tool.mcpUrl} headers={tool.mcpHeaders}/>)}
        {!tool.description && !(tool.type === 'agent' && tool.agentShareToken) && tool.type !== 'mcp_server' && (<p className="mt-table-empty">No details available.</p>)}
      </div>
    </motion.div>);
};
const MarketplaceView: React.FC = () => {
    const isAgencyOwner = readIsAgencyOwner();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tools, setTools] = useState<MyTool[]>([]);
    const [installedIds, setInstalledIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [installingId, setInstallingId] = useState<string | null>(null);
    const [pendingInstall, setPendingInstall] = useState<MyTool | null>(null);
    const [pendingInstallMode, setPendingInstallMode] = useState<'personal' | 'agency'>('personal');
    // Detail selection lives in the URL, same pattern as InstalledView's
    // ?view=installed&toolId=<id> — a refresh or a shared link resolves once
    // `tools` has loaded, rather than being lost like the old popup's local state.
    const toolId = searchParams.get('toolId');
    const selectedTool = toolId ? (tools.find(t => t.id === toolId) ?? null) : null;
    const [toast, setToast] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);
    const [typeFilter, setTypeFilter] = useState<'all' | ToolType>('all');
    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([apiFetchMarketplaceTools(), apiFetchInstalledTools().catch(() => [])])
            .then(([marketplaceTools, installed]) => {
            setTools(marketplaceTools);
            setInstalledIds(new Set(installed.map(t => t.id)));
        })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);
    const runInstall = useCallback(async (tool: MyTool, fieldValues?: Record<string, string>, mode: 'personal' | 'agency' = 'personal') => {
        setInstallingId(tool.id);
        try {
            if (mode === 'agency') {
                await apiInstallToolForAgency(tool.id, fieldValues);
                setToast({ type: 'success', message: `${tool.name} installed for your agency.` });
            }
            else {
                await apiInstallTool(tool.id, fieldValues);
                setToast({ type: 'success', message: `${tool.name} installed successfully.` });
            }
            setPendingInstall(null);
            setInstalledIds(prev => new Set(prev).add(tool.id));
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setToast({ type: 'error', message: msg === 'already_installed' ? 'This tool is already installed.' : (msg || 'Failed to install tool.') });
        }
        finally {
            setInstallingId(null);
            setTimeout(() => setToast(null), 4000);
        }
    }, []);
    const handleAgentInstallForAgency = async (tool: MyTool) => {
        setInstallingId(tool.id);
        try {
            const summary = summarizeAgentAgencyInstall(await apiInstallAgentForAgency(tool.id));
            const parts = [`${summary.created} agent(s) created for your agency.`];
            if (summary.pendingSetup)
                parts.push(`${summary.pendingSetup} teammate(s) aren't set up yet and will get it automatically at their next sign-in.`);
            if (summary.alreadyInstalled)
                parts.push(`${summary.alreadyInstalled} already had it.`);
            if (summary.failed)
                parts.push(`${summary.failed} couldn't be created.`);
            setToast({ type: summary.failed ? 'error' : 'success', message: parts.join(' ') });
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setToast({ type: 'error', message: msg || 'Failed to install agent for agency.' });
        }
        finally {
            setInstallingId(null);
            setTimeout(() => setToast(null), 5000);
        }
    };
    const handleInstallClick = (tool: MyTool, mode: 'personal' | 'agency' = 'personal') => {
        if (installedIds.has(tool.id))
            return;
        if (tool.type === 'agent' && tool.agentShareToken) {
            if (mode === 'agency') {
                handleAgentInstallForAgency(tool);
                return;
            }
            navigate(ROUTES.SHARE_AGENT_RESOLVE.replace(':token', tool.agentShareToken));
            return;
        }
        const relevantHeaders = (tool.mcpHeaders ?? []).filter(h => mode === 'personal' || h.scope !== 'per_user');
        const hasRequiredFields = relevantHeaders.some(h => h.requiredFromInstaller);
        // A published listing is community-shared (came from another agency, or
        // at least isn't confirmed same-agency) — always confirm via the trust
        // checkbox before installing, not only when there happen to be required
        // MCP credential fields to collect.
        if ((tool.type === 'mcp_server' && hasRequiredFields) || tool.visibility === 'published') {
            setPendingInstallMode(mode);
            setPendingInstall(tool);
        }
        else {
            runInstall(tool, undefined, mode);
        }
    };
    if (loading) {
        return (<div className="mt-installed-grid">
        {Array.from({ length: 6 }).map((_, i) => (<SkeletonLoader key={i} height={180} lines={3}/>))}
      </div>);
    }
    if (error) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">⚠️</div>
        <div className="mt-empty-state__title">Failed to load</div>
        <div className="mt-empty-state__subtitle">{error}</div>
      </div>);
    }
    if (selectedTool) {
        return (<>
        <AnimatePresence mode="wait">
          <MarketplaceToolDetail key={selectedTool.id} tool={selectedTool} isAgencyOwner={isAgencyOwner} installing={installingId === selectedTool.id} installed={installedIds.has(selectedTool.id)} onBack={() => setSearchParams({ view: 'marketplace' })} onInstall={() => handleInstallClick(selectedTool, 'personal')} onInstallForAgency={() => handleInstallClick(selectedTool, 'agency')}/>
        </AnimatePresence>

        <AnimatePresence>
          {pendingInstall && (<MarketplaceInstallModal tool={pendingInstall} mode={pendingInstallMode} installing={installingId === pendingInstall.id} onCancel={() => setPendingInstall(null)} onConfirm={values => runInstall(pendingInstall, values, pendingInstallMode)}/>)}
        </AnimatePresence>

        <AnimatePresence>
          {toast && (<motion.div className={toast.type === 'success' ? 'success-toast' : 'error-toast'} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
              <span>{toast.message}</span>
            </motion.div>)}
        </AnimatePresence>
      </>);
    }
    if (tools.length === 0) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">🛍️</div>
        <div className="mt-empty-state__title">Nothing here yet</div>
        <div className="mt-empty-state__subtitle">Tools shared by your agency or published to everyone will appear here</div>
      </div>);
    }
    const browseTypesPresent = Array.from(new Set(tools.map(t => t.type)));
    const browseFilteredTools = typeFilter === 'all' ? tools : tools.filter(t => t.type === typeFilter);
    return (<>
      {browseTypesPresent.length > 1 && (<div className="mt-browse-filters">
          <select className="mt-type-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value as 'all' | ToolType)} aria-label="Filter by type">
            <option value="all">All Types</option>
            {browseTypesPresent.map(t => (<option key={t} value={t}>{TOOL_TYPES.find(ti => ti.type === t)!.label}</option>))}
          </select>
        </div>)}
      {browseFilteredTools.length === 0 ? (<div className="mt-empty-state">
          <div className="mt-empty-state__icon">🔍</div>
          <div className="mt-empty-state__title">No tools of this type</div>
          <div className="mt-empty-state__subtitle">Try a different type filter.</div>
        </div>) : (<div className="mt-installed-grid">
        {browseFilteredTools.map(tool => (<MarketplaceCard key={tool.id} tool={tool} onViewDetail={() => setSearchParams({ view: 'marketplace', toolId: tool.id })} installing={installingId === tool.id} installed={installedIds.has(tool.id)} onInstall={() => handleInstallClick(tool, 'personal')} onInstallForAgency={() => handleInstallClick(tool, 'agency')}/>))}
      </div>)}

      <AnimatePresence>
        {pendingInstall && (<MarketplaceInstallModal tool={pendingInstall} mode={pendingInstallMode} installing={installingId === pendingInstall.id} onCancel={() => setPendingInstall(null)} onConfirm={values => runInstall(pendingInstall, values, pendingInstallMode)}/>)}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (<motion.div className={toast.type === 'success' ? 'success-toast' : 'error-toast'} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
            <span>{toast.message}</span>
          </motion.div>)}
      </AnimatePresence>
    </>);
};
function toolAuthHeaders() {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}
interface ToolComment {
    id: string;
    authorName: string;
    message: string;
    // Set only on the note auto-posted alongside a Request Changes/Reject verdict -
    // lets this thread flag it as the official reason your submission was
    // returned, not an ordinary reply. Mirrors ReviewQueuePage's ReviewComment.
    relatedStatus?: 'changes_requested' | 'rejected' | null;
    createdAt: string;
}
function toolTimeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 60)
        return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}
function toolInitials(name: string) { return name.split(' ').map(p => p[0] ?? '').join('').toUpperCase().slice(0, 2); }
function toolAvatarColor(name: string) {
    const colors = ['#8b5cf6', '#14b8a6', '#f59e0b', '#3b82f6', '#ec4899', '#22d3ee'];
    let h = 0;
    for (let i = 0; i < name.length; i++)
        h = (h * 31 + name.charCodeAt(i)) & 0xffff;
    return colors[h % colors.length];
}
const PUBLISH_STATUS_CONFIG: Record<string, {
    label: string;
    color: string;
    bg: string;
    border: string;
}> = {
    pending: { label: '⏳ Pending Review', color: '#fbbf24', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' },
    approved: { label: '✓ Approved', color: '#34d399', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)' },
    rejected: { label: '✗ Rejected', color: '#f87171', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.28)' },
    changes_requested: { label: '⚠ Changes Requested', color: '#fb923c', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)' },
};
const ShareModal: React.FC<{
    toolId: string;
    toolName: string;
    onClose: () => void;
}> = ({ toolId, toolName, onClose }) => {
    const [shares, setShares] = useState<Share[]>([]);
    const [loadingShares, setLoadingShares] = useState(true);
    const [scope, setScope] = useState<ShareScope>('agency');
    const [creating, setCreating] = useState(false);
    const [busyShareId, setBusyShareId] = useState<string | null>(null);
    const [copiedToken, setCopiedToken] = useState<string | null>(null);
    const [error, setError] = useState('');
    const loadShares = useCallback(() => {
        setLoadingShares(true);
        getShares(toolId)
            .then(setShares)
            .catch(() => setShares([]))
            .finally(() => setLoadingShares(false));
    }, [toolId]);
    useEffect(() => { loadShares(); }, [loadShares]);
    const shareUrl = (token: string) => `${window.location.origin}/share/${token}`;
    const handleCopy = (token: string) => {
        navigator.clipboard?.writeText(shareUrl(token)).catch(() => { });
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2000);
    };
    const handleCreate = async () => {
        setCreating(true);
        setError('');
        try {
            await createShare(toolId, scope);
            loadShares();
        }
        catch (err: any) {
            setError(err.message || 'Failed to create share link');
        }
        finally {
            setCreating(false);
        }
    };
    const handleRevoke = async (shareId: string) => {
        setBusyShareId(shareId);
        try {
            await revokeShare(shareId);
            loadShares();
        }
        catch (err: any) {
            setError(err.message || 'Failed to revoke share link');
        }
        finally {
            setBusyShareId(null);
        }
    };
    const handleRegenerate = async (shareId: string) => {
        setBusyShareId(shareId);
        try {
            await regenerateShare(shareId);
            loadShares();
        }
        catch (err: any) {
            setError(err.message || 'Failed to regenerate share link');
        }
        finally {
            setBusyShareId(null);
        }
    };
    const activeShares = shares.filter(s => s.isActive);
    return (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget)
        onClose(); }}>
      <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
        <div className="mt-modal__header">
          <div className="mt-modal__header-info">
            <h3 className="mt-modal__title">Share "{toolName}"</h3>
          </div>
          <button type="button" className="mt-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="mt-step-subtitle">
          Anyone who opens the link installs their own independent copy automatically — no separate
          import step. Works on drafts; sharing doesn't go through review.
        </p>

        {activeShares.length === 0 && (<div className="mt-form">
            <div className="mt-field mt-field--full">
              <label className="mt-label">Who can use this link?</label>
              <div className="mt-visibility-group" style={{ marginTop: 6 }}>
                <button type="button" className={`mt-visibility-btn ${scope === 'agency' ? 'mt-visibility-btn--active' : ''}`} onClick={() => setScope('agency')}>
                  Anyone in this agency with this link
                </button>
                <button type="button" className={`mt-visibility-btn ${scope === 'anyone' ? 'mt-visibility-btn--active' : ''}`} onClick={() => setScope('anyone')}>
                  Anyone with this link
                </button>
              </div>
            </div>
          </div>)}

        {error && <p style={{ color: '#f87171', fontSize: 12.5, margin: '4px 0' }}>{error}</p>}

        {loadingShares ? (<p className="mt-step-subtitle">Loading…</p>) : activeShares.length > 0 ? (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeShares.map(s => (<div key={s.id} style={{
                    background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                    borderRadius: 9, padding: '10px 13px', display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="mt-badge mt-badge--visibility">
                    {s.scope === 'agency' ? 'Anyone in this agency' : 'Anyone with this link'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input readOnly value={shareUrl(s.token)} className="mt-input" style={{ fontSize: 12, flex: 1 }} onClick={e => (e.target as HTMLInputElement).select()}/>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => handleCopy(s.token)}>
                    {copiedToken === s.token ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
                <div className="mt-actions" style={{ marginTop: 0 }}>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" disabled={busyShareId === s.id} onClick={() => handleRegenerate(s.id)}>
                    {busyShareId === s.id ? 'Working…' : 'Regenerate'}
                  </button>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline mt-btn--sm" disabled={busyShareId === s.id} onClick={() => handleRevoke(s.id)}>
                    {busyShareId === s.id ? 'Working…' : 'Revoke'}
                  </button>
                </div>
              </div>))}
          </div>) : null}

        {activeShares.length === 0 && (<div className="mt-actions">
            <button type="button" className="mt-btn mt-btn--secondary" onClick={onClose} disabled={creating}>Cancel</button>
            <button type="button" className="mt-btn mt-btn--primary" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating…' : 'Create link'}
            </button>
          </div>)}
      </motion.div>
    </motion.div>);
};
const ToolDetailPanel: React.FC<{
    tool: MyTool;
    onClose: () => void;
}> = ({ tool, onClose }) => {
    const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
    const displayIcon = tool.icon.trim() || typeInfo.icon;
    const statusCfg = tool.publishStatus ? PUBLISH_STATUS_CONFIG[tool.publishStatus] : null;
    const [comments, setComments] = useState<ToolComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentsError, setCommentsError] = useState('');
    const [newComment, setNewComment] = useState('');
    const [sending, setSending] = useState(false);
    const feedRef = useRef<HTMLDivElement>(null);
    const readOnlySkillEditorRef = useRef<HTMLTextAreaElement>(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    useEffect(() => {
        if (!tool.publishRequestId)
            return;
        setCommentsLoading(true);
        setCommentsError('');
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${tool.publishRequestId}/comments`, { headers: toolAuthHeaders() })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(json => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            setComments(list.map((c: any): ToolComment => ({
                id: String(c.id), authorName: c.authorName ?? 'Unknown',
                message: c.message ?? '', relatedStatus: c.relatedStatus ?? null,
                createdAt: c.createdAt ?? new Date().toISOString(),
            })));
        })
            .catch((status) => {
                setComments([]);
                setCommentsError(status === 403
                    ? "Couldn't load review comments - you may not have access to this thread."
                    : "Couldn't load review comments. Try again later.");
            })
            .finally(() => setCommentsLoading(false));
    }, [tool.publishRequestId]);
    useEffect(() => {
        if (feedRef.current)
            feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }, [comments]);
    const handleSend = async () => {
        const text = newComment.trim();
        if (!text || sending || !tool.publishRequestId)
            return;
        setSending(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${tool.publishRequestId}/comments`, {
                method: 'POST', headers: toolAuthHeaders(), body: JSON.stringify({ message: text }),
            });
            if (res.ok) {
                const data = await res.json();
                setComments(prev => [...prev, {
                        id: String(data.id ?? Date.now()), authorName: data.authorName ?? 'You',
                        message: text, createdAt: data.createdAt ?? new Date().toISOString(),
                    }]);
                setNewComment('');
            }
        }
        catch { }
        finally {
            setSending(false);
        }
    };
    return (<motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }} transition={{ duration: 0.22 }} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: 2 }}>

        
        <button onClick={onClose} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--text-tertiary)', fontSize: 12, fontWeight: 500,
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, marginBottom: 18, fontFamily: 'inherit',
        }}>
          ← Back to My Creations
        </button>

        
        <div className="mt-detail-card">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
            <span style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{displayIcon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <h2 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0 }}>{tool.name}</h2>
                <span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {statusCfg && (<span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, color: statusCfg.color }}>
                    {statusCfg.label}
                  </span>)}
                <span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span>
                {tool.category && <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{tool.category}</span>}
              </div>
            </div>
            {tool.type === 'skill' && (<button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => setShareModalOpen(true)}>
                🔗 Share
              </button>)}
          </div>

          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tool.description && (<div>
                <div className="mt-detail-field-label">Description</div>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.65, margin: 0 }}>{tool.description}</p>
              </div>)}
            {tool.skills && tool.skills.trim() && (<div>
                <div className="mt-detail-field-label">Capabilities</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {tool.skills.split('\n').map(s => s.trim()).filter(Boolean).map((s, i) => (<li key={i} style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, marginBottom: 3 }}>{s}</li>))}
                </ul>
              </div>)}
            {tool.kbFileNames.length > 0 && (<div>
                <div className="mt-detail-field-label">Knowledge Base Files</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {tool.kbFileNames.map(f => (<span key={f} style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.22)', color: '#a78bfa', fontSize: 11, padding: '2px 8px', borderRadius: 5 }}>📎 {f}</span>))}
                </div>
              </div>)}
            {tool.type === 'skill' && tool.skillContent && (<div>
                <div className="mt-detail-field-label">Skill content</div>
                <SkillEditorBody mode="preview" onModeChange={() => { }} body={tool.skillContent} onBodyChange={() => { }} textEditorRef={readOnlySkillEditorRef} insertMd={() => { }} readOnly hideModeTabs/>
              </div>)}
            {tool.type === 'agent' && tool.agentShareToken && (<AgentBundlePreview token={tool.agentShareToken}/>)}
            {tool.type === 'mcp_server' && (<McpDetailBody url={tool.mcpUrl} headers={tool.mcpHeaders}/>)}
            <div>
              <div className="mt-detail-field-label">Created</div>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(tool.createdAt)}</span>
            </div>
          </div>
        </div>

        
        {tool.publishRequestId ? (<div className="mt-detail-card" style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Review Comments {comments.length > 0 && `(${comments.length})`}
              </span>
            </div>

            
            <div ref={feedRef} style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
              {commentsLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>Loading…</p>) : commentsError ? (<p style={{ color: '#f87171', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>{commentsError}</p>) : comments.length === 0 ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>No review comments yet.</p>) : comments.map(c => {
                const color = toolAvatarColor(c.authorName);
                // The note a reviewer attaches to Request Changes/Reject gets flagged here
                // too, so it doesn't read as an ordinary reply - this is the actual reason
                // your submission came back, not small talk in the thread.
                const verdictCfg = c.relatedStatus ? PUBLISH_STATUS_CONFIG[c.relatedStatus] : null;
                return (<div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: color + '25', border: `1.5px solid ${color}55`,
                        color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700,
                    }}>
                      {toolInitials(c.authorName)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600 }}>{c.authorName}</span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{toolTimeAgo(c.createdAt)}</span>
                        {verdictCfg && (<span style={{
                            padding: '1px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                            background: verdictCfg.bg, border: `1px solid ${verdictCfg.border}`, color: verdictCfg.color,
                        }}>
                              {verdictCfg.label}
                            </span>)}
                      </div>
                      <div style={{
                        background: verdictCfg ? verdictCfg.bg : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${verdictCfg ? verdictCfg.border : 'rgba(255,255,255,0.07)'}`,
                        borderRadius: '0 8px 8px 8px', padding: '7px 10px',
                    }}>
                        <p style={{ color: 'var(--text-secondary)', fontSize: 12.5, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{c.message}</p>
                      </div>
                    </div>
                  </div>);
            })}
            </div>

            
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
        } }} placeholder="Reply to reviewer… (Ctrl+Enter to send)" rows={2} style={{
                flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: 9, color: 'var(--text-primary)', fontSize: 13, padding: '8px 11px',
                fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box',
            }}/>
              <button onClick={handleSend} disabled={!newComment.trim() || sending || !!commentsError} className="mt-btn mt-btn--primary" style={{ padding: '8px 14px', borderRadius: 9, flexShrink: 0, fontSize: 13, opacity: !newComment.trim() || sending || !!commentsError ? 0.45 : 1 }}>
                Send
              </button>
            </div>
          </div>) : (<div className="mt-detail-card" style={{ marginTop: 14, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: 0 }}>Submit this skill for review to see reviewer comments here.</p>
          </div>)}
      </div>
      <AnimatePresence>
        {shareModalOpen && (<ShareModal toolId={tool.id} toolName={tool.name} onClose={() => setShareModalOpen(false)}/>)}
      </AnimatePresence>
    </motion.div>);
};
type CreationsTab = 'all' | Visibility;
function visLabel(v: Visibility): string {
    if (v === 'just_me')
        return 'Personal';
    if (v === 'my_agency')
        return 'My Agency';
    return 'Published';
}
interface CreationsViewProps {
    tools: MyTool[];
    loading: boolean;
    onDelete: (id: string) => void;
    onEdit: (tool: MyTool) => void;
    onUpdate: (id: string, changes: Partial<MyTool>) => void;
    onInstall: (tool: MyTool) => void;
    installingId: string | null;
    onUninstallForAgency: (tool: MyTool) => void;
    onViewDetail: (tool: MyTool) => void;
    onRefetchTools: () => void;
}
const CreationsView: React.FC<CreationsViewProps> = ({ tools, loading, onDelete, onEdit, onUpdate, onInstall, installingId, onUninstallForAgency, onViewDetail, onRefetchTools }) => {
    const isAgencyOwner = readIsAgencyOwner();
    const [, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<CreationsTab>('all');
    const [typeFilter, setTypeFilter] = useState<'all' | ToolType>('all');
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const [menuBtnRect, setMenuBtnRect] = useState<{
        top: number;
        bottom: number;
        right: number;
    } | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [shareToolId, setShareToolId] = useState<string | null>(null);
    const [shareAgentId, setShareAgentId] = useState<string | null>(null);
    const [shareResolving, setShareResolving] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const openShareModal = async (tool: MyTool) => {
        closeMenu();
        setShareToolId(tool.id);
        if (tool.type !== 'agent')
            return;
        setShareAgentId(null);
        if (!tool.agentShareToken)
            return;
        setShareResolving(true);
        try {
            const { source_agent_id } = await resolveAgentShare(tool.agentShareToken);
            setShareAgentId(source_agent_id ?? null);
        }
        catch {
            setShareAgentId(null);
        }
        finally {
            setShareResolving(false);
        }
    };
    const openMenu = useCallback((id: string, btn: HTMLButtonElement) => {
        const rect = btn.getBoundingClientRect();
        setMenuBtnRect({ top: rect.top, bottom: rect.bottom, right: rect.right });
        setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
        setOpenMenuId(id);
    }, []);
    const closeMenu = useCallback(() => {
        setOpenMenuId(null);
        setMenuPos(null);
        setMenuBtnRect(null);
    }, []);
    // The menu's height depends on which items apply to the selected tool, so
    // it can't be predicted before it renders. Measure it once mounted and,
    // if opening below the button would run past the viewport (the case for
    // any row near the bottom of a scrolled page), flip it above the button
    // instead of leaving it rendered off-screen.
    useLayoutEffect(() => {
        if (!openMenuId || !menuBtnRect || !menuRef.current)
            return;
        const margin = 8;
        const menuHeight = menuRef.current.offsetHeight;
        if (menuBtnRect.bottom + 4 + menuHeight > window.innerHeight - margin) {
            setMenuPos(prev => prev ? { ...prev, top: Math.max(margin, menuBtnRect.top - menuHeight - 4) } : prev);
        }
    }, [openMenuId, menuBtnRect]);
    useEffect(() => {
        if (!openMenuId)
            return;
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node))
                closeMenu();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openMenuId, closeMenu]);
    if (loading) {
        return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SkeletonLoader height={70} lines={1}/>
        {Array.from({ length: 5 }).map((_, i) => (<SkeletonLoader key={i} height={48} lines={1}/>))}
      </div>);
    }
    if (tools.length === 0) {
        return (<div className="mt-empty-state">
        <div className="mt-empty-state__icon">🛠️</div>
        <div className="mt-empty-state__title">No tools yet</div>
        <div className="mt-empty-state__subtitle">Create your first tool to see it here</div>
        <button type="button" className="mt-btn mt-btn--primary" onClick={() => setSearchParams({ view: 'create' })}>
          Create New
        </button>
      </div>);
    }
    const counts = {
        total: tools.length,
        personal: tools.filter(t => t.visibility === 'just_me').length,
        agency: tools.filter(t => t.visibility === 'my_agency').length,
        published: tools.filter(t => t.visibility === 'published').length,
    };
    const filteredTools = (activeTab === 'all' ? tools : tools.filter(t => t.visibility === activeTab))
        .filter(t => typeFilter === 'all' || t.type === typeFilter);
    const typesPresent = Array.from(new Set(tools.map(t => t.type)));
    const CAT_TABS: {
        key: CreationsTab;
        label: string;
        count: number;
    }[] = [
        { key: 'all', label: 'All', count: counts.total },
        { key: 'just_me', label: 'Personal', count: counts.personal },
        { key: 'my_agency', label: 'Agency', count: counts.agency },
        { key: 'published', label: 'Published', count: counts.published },
    ];
    return (<div className="mt-creations-layout">
      <div className="mt-stat-cards">
        <div className="mt-stat-card mt-stat-card--total">
          <span className="mt-stat-card__value">{counts.total}</span>
          <span className="mt-stat-card__label">Total</span>
        </div>
        <div className="mt-stat-card mt-stat-card--personal">
          <span className="mt-stat-card__value">{counts.personal}</span>
          <span className="mt-stat-card__label">📝 Personal</span>
        </div>
        <div className="mt-stat-card mt-stat-card--agency">
          <span className="mt-stat-card__value">{counts.agency}</span>
          <span className="mt-stat-card__label">🏢 Agency</span>
        </div>
        <div className="mt-stat-card mt-stat-card--published">
          <span className="mt-stat-card__value">{counts.published}</span>
          <span className="mt-stat-card__label">🌐 Published</span>
        </div>
      </div>

      <div className="mt-table-section">
        <div className="mt-cat-tabs">
          {CAT_TABS.map(tab => (<button key={tab.key} type="button" className={`mt-cat-tab ${activeTab === tab.key ? 'mt-cat-tab--active' : ''}`} onClick={() => setActiveTab(tab.key)}>
              {tab.label}
              <span className="mt-cat-tab__count">{tab.count}</span>
            </button>))}
          {typesPresent.length > 1 && (<select className="mt-type-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value as 'all' | ToolType)} aria-label="Filter by type">
              <option value="all">All Types</option>
              {typesPresent.map(t => (<option key={t} value={t}>{TOOL_TYPES.find(ti => ti.type === t)!.label}</option>))}
            </select>)}
        </div>

        <div className="mt-table-wrapper">
          {filteredTools.length === 0 ? (<div className="mt-table-empty">No tools in this category.</div>) : (<table className="mt-table">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th>Visibility</th><th>Status</th>
                  <th>Date</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTools.map(tool => {
                const typeInfo = TOOL_TYPES.find(t => t.type === tool.type)!;
                const displayIcon = tool.icon.trim() || typeInfo.icon;
                return (<tr key={tool.id} style={{ cursor: 'pointer' }} onClick={() => onViewDetail(tool)}>
                      <td>
                        <div className="mt-table-name-cell">
                          <span className="mt-table-icon">{displayIcon}</span>
                          <div>
                            <div className="mt-table-name">{tool.name}</div>
                            {tool.category && <div className="mt-table-category">{tool.category}</div>}
                          </div>
                        </div>
                      </td>
                      <td><span className={`mt-badge mt-badge--type mt-badge--type-${tool.type}`}>{typeInfo.label}</span></td>
                      <td><span className="mt-badge mt-badge--visibility">{visLabel(tool.visibility)}</span></td>
                      <td>{(() => {
                        if (tool.visibility === 'published')
                            return <span className="mt-badge mt-badge--status mt-badge--published">Published</span>;
                        if (tool.publishStatus === 'pending')
                            return <span className="mt-badge mt-badge--status mt-badge--pending">⏳ Pending Review</span>;
                        if (tool.publishStatus === 'changes_requested')
                            return <span className="mt-badge mt-badge--status mt-badge--changes">Changes Requested</span>;
                        if (tool.publishStatus === 'rejected')
                            return <span className="mt-badge mt-badge--status mt-badge--rejected">Rejected</span>;
                        return <span className="mt-badge mt-badge--status">Active</span>;
                    })()}</td>
                      <td><span className="mt-table-date">{formatDate(tool.createdAt)}</span></td>
                      <td>
                        <button type="button" className="mt-kebab-btn" aria-label="Actions" onClick={e => { e.stopPropagation(); openMenu(tool.id, e.currentTarget); }}>
                          ⋯
                        </button>
                      </td>
                    </tr>);
            })}
              </tbody>
            </table>)}
        </div>
      </div>

      {openMenuId && menuPos && ReactDOM.createPortal(<div ref={menuRef} className="mt-action-menu" style={{ top: menuPos.top, left: menuPos.left }}>
          {(() => {
                const tool = tools.find(t => t.id === openMenuId);
                if (!tool)
                    return null;
                return (<>
                <button type="button" className="mt-action-menu__item" onClick={() => { onEdit(tool); closeMenu(); }}>
                  ✏️ Edit
                </button>
                {tool.type !== 'agent' && (<button type="button" className="mt-action-menu__item" disabled={installingId === tool.id} onClick={() => { onInstall(tool); closeMenu(); }} title="Install this for your own AI agent">
                    {installingId === tool.id ? 'Installing…' : '▶️ Install'}
                  </button>)}
                <button type="button" className="mt-action-menu__item" onClick={() => openShareModal(tool)}>
                  🔗 Share
                </button>
                <div className="mt-action-menu__divider"/>
                <button type="button" className="mt-action-menu__item mt-action-menu__item--danger" onClick={() => { setConfirmDeleteId(tool.id); closeMenu(); }}>
                  🗑️ Delete
                </button>
              </>);
            })()}
        </div>, document.body)}

      <AnimatePresence>
        {confirmDeleteId && (() => {
            const tool = tools.find(t => t.id === confirmDeleteId);
            if (!tool)
                return null;
            return (<motion.div className="mt-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={e => { if (e.target === e.currentTarget)
                setConfirmDeleteId(null); }}>
              <motion.div className="mt-modal" initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
                <div className="mt-modal__header">
                  <div className="mt-modal__header-info">
                    <div className="mt-modal__icon">🗑️</div>
                    <h3 className="mt-modal__title">Delete "{tool.name}"?</h3>
                  </div>
                  <button type="button" className="mt-modal__close" onClick={() => setConfirmDeleteId(null)} aria-label="Close">×</button>
                </div>
                <p className="mt-step-subtitle">This can't be undone.</p>
                <div className="mt-actions">
                  <button type="button" className="mt-btn mt-btn--secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                  <button type="button" className="mt-btn mt-btn--secondary mt-btn--danger-outline" onClick={() => { onDelete(tool.id); setConfirmDeleteId(null); }}>
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>);
        })()}
      </AnimatePresence>

      {shareToolId && (() => {
            const tool = tools.find(t => t.id === shareToolId);
            if (!tool)
                return null;
            const closeShare = () => { setShareToolId(null); setShareAgentId(null); onRefetchTools(); };
            if (tool.type === 'agent') {
                if (shareResolving || !shareAgentId)
                    return null;
                return (<AgentShareMenuModal agentId={shareAgentId} agentName={tool.name} isAgencyOwner={isAgencyOwner} existingTool={{ id: tool.id, visibility: tool.visibility, publishStatus: tool.publishStatus, isAgencyInstalled: tool.isAgencyInstalled }} onShareWithAgency={() => onUpdate(tool.id, { visibility: 'my_agency' })} onUninstallForAgency={() => onUninstallForAgency(tool)} onClose={closeShare}/>);
            }
            return (<ToolShareMenuModal tool={{ id: tool.id, name: tool.name, type: tool.type as 'skill' | 'mcp_server', visibility: tool.visibility, publishStatus: tool.publishStatus, isAgencyInstalled: tool.isAgencyInstalled }} isAgencyOwner={isAgencyOwner} onShareWithAgency={() => onUpdate(tool.id, { visibility: 'my_agency' })} onUninstallForAgency={() => onUninstallForAgency(tool)} onClose={closeShare}/>);
        })()}
    </div>);
};
const MyToolsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const view = (searchParams.get('view') as WizardView) || 'creations';
    const location = useLocation();
    const navigate = useNavigate();
    const [tools, setTools] = useState<MyTool[]>([]);
    const [loadingTools, setLoadingTools] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [wizardStep, setWizardStep] = useState(1);
    const [selectedType, setSelectedType] = useState<ToolType | null>(null);
    const [name, setName] = useState('');
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [icon, setIcon] = useState('');
    const [description, setDescription] = useState('');
    const [skills, setSkills] = useState('');
    const [skillContent, setSkillContent] = useState('');
    const [kbFiles, setKbFiles] = useState<File[]>([]);
    const [visibility, setVisibility] = useState<Visibility>('just_me');
    const [existingKbFileNames, setExistingKbFileNames] = useState<string[]>([]);
    const [existingSkillKbFiles, setExistingSkillKbFiles] = useState<{
        id: string;
        filename: string;
    }[]>([]);
    const [mcpUrl, setMcpUrl] = useState('');
    const [mcpHeaders, setMcpHeaders] = useState<McpHeaderField[]>([]);
    const [agentShareToken, setAgentShareToken] = useState('');
    const [agentId, setAgentId] = useState('');
    const [agentPreview, setAgentPreview] = useState<AgentShareSnapshot | null>(null);
    const [agentFrozenSnapshot, setAgentFrozenSnapshot] = useState<AgentShareSnapshot | null>(null);
    useEffect(() => {
        if (view !== 'creations')
            return;
        setLoadingTools(true);
        apiFetchMyTools()
            .then(setTools)
            .catch(() => setTools([]))
            .finally(() => setLoadingTools(false));
    }, [view]);
    useEffect(() => {
        if (view !== 'create') {
            setEditingId(null);
            setWizardStep(1);
            setSelectedType(null);
            setName('');
            setCategory(CATEGORIES[0]);
            setIcon('');
            setDescription('');
            setSkills('');
            setSkillContent('');
            setKbFiles([]);
            setExistingKbFileNames([]);
            setExistingSkillKbFiles([]);
            setVisibility('just_me');
            setMcpUrl('');
            setMcpHeaders([]);
            setAgentShareToken('');
            setAgentId('');
            setAgentPreview(null);
            setAgentFrozenSnapshot(null);
        }
    }, [view]);
    useEffect(() => {
        const prefillAgent = (location.state as {
            prefillAgent?: {
                agentId: string;
            };
        } | null)?.prefillAgent;
        if (view === 'create' && prefillAgent) {
            setSelectedType('agent');
            setAgentId(prefillAgent.agentId);
            setWizardStep(2);
            navigate(location.pathname + location.search, { replace: true, state: null });
        }
    }, [view, location.state]);
    useEffect(() => {
        const prefillSkill = (location.state as {
            prefillSkill?: {
                name: string;
                skillContent: string;
            };
        } | null)?.prefillSkill;
        if (view === 'create' && prefillSkill) {
            setSelectedType('skill');
            setName(prefillSkill.name);
            setSkillContent(prefillSkill.skillContent);
            setDescription(extractSkillDescription(prefillSkill.skillContent) || '');
            setWizardStep(2);
            navigate(location.pathname + location.search, { replace: true, state: null });
        }
    }, [view, location.state]);
    const handleEdit = (tool: MyTool) => {
        setEditingId(tool.id);
        setSelectedType(tool.type);
        setName(tool.name);
        setCategory(tool.category || CATEGORIES[0]);
        setIcon(tool.icon);
        setDescription(tool.description);
        setSkills(tool.skills);
        setSkillContent(tool.skillContent ?? '');
        setKbFiles([]);
        setExistingKbFileNames(tool.kbFileNames);
        setExistingSkillKbFiles([]);
        if (tool.type === 'skill') {
            apiGetSkillKbFiles(tool.id).then(setExistingSkillKbFiles).catch(() => setExistingSkillKbFiles([]));
        }
        setVisibility(tool.visibility);
        setMcpUrl(tool.mcpUrl ?? '');
        setMcpHeaders(tool.mcpHeaders ?? []);
        setAgentShareToken(tool.agentShareToken ?? '');
        setAgentId('');
        setAgentPreview(null);
        setAgentFrozenSnapshot(null);
        if (tool.type === 'agent' && tool.agentShareToken) {
            resolveAgentShare(tool.agentShareToken).then(({ source_agent_id, snapshot }) => {
                setAgentId(source_agent_id ?? '');
                setAgentFrozenSnapshot(snapshot);
            }).catch(() => { });
        }
        setWizardStep(2);
        setSearchParams({ view: 'create' });
    };
    const handleUpdate = useCallback(async (id: string, changes: Partial<MyTool>) => {
        const tool = tools.find(t => t.id === id);
        if (!tool)
            return;
        const merged = { ...tool, ...changes };
        setTools(prev => prev.map(t => t.id === id ? merged : t));
        try {
            await apiUpsertTool({
                id: Number(id),
                type: TOOL_TYPE_TO_INT[merged.type],
                name: merged.name,
                category: merged.category,
                icon: merged.icon,
                description: merged.description,
                skills: merged.skills,
                kbFileNames: merged.kbFileNames,
                visibility: VISIBILITY_TO_INT[merged.visibility],
            });
        }
        catch {
            setTools(prev => prev.map(t => t.id === id ? tool : t));
        }
    }, [tools]);
    const [selectedTool, setSelectedTool] = useState<MyTool | null>(null);
    const [publishToast, setPublishToast] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);
    const [installingCreationId, setInstallingCreationId] = useState<string | null>(null);
    const [pendingCreationInstall, setPendingCreationInstall] = useState<InstallableTool | null>(null);
    const runCreationInstall = useCallback(async (tool: InstallableTool, fieldValues?: Record<string, string>) => {
        setInstallingCreationId(tool.id);
        try {
            await apiInstallTool(tool.id, fieldValues);
            setPendingCreationInstall(null);
            setPublishToast({ type: 'success', message: `${tool.name} installed successfully.` });
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setPublishToast({ type: 'error', message: msg === 'already_installed' ? 'You already have this installed.' : (msg || 'Failed to install tool.') });
        }
        finally {
            setInstallingCreationId(null);
            setTimeout(() => setPublishToast(null), 4000);
        }
    }, []);
    const handleInstall = useCallback((tool: InstallableTool) => {
        if (tool.type === 'agent')
            return;
        const hasRequiredFields = (tool.mcpHeaders ?? []).some(h => h.requiredFromInstaller);
        if (tool.type === 'mcp_server' && hasRequiredFields) {
            setPendingCreationInstall(tool);
        }
        else {
            runCreationInstall(tool);
        }
    }, [runCreationInstall]);
    const handleUninstallForAgency = useCallback(async (tool: MyTool) => {
        try {
            await apiUninstallToolForAgency(tool.id);
            setTools(prev => prev.map(t => t.id === tool.id ? { ...t, isAgencyInstalled: false } : t));
            setPublishToast({ type: 'success', message: `${tool.name} uninstalled for your agency.` });
        }
        catch (err: unknown) {
            const msg = err instanceof Error ? err.message : '';
            setPublishToast({ type: 'error', message: msg || 'Failed to uninstall for agency.' });
        }
        setTimeout(() => setPublishToast(null), 4000);
    }, []);
    const handleSave = useCallback(async () => {
        if (!selectedType)
            return;
        const willAutoInstall = !editingId && selectedType !== 'agent';
        setSaving(true);
        const kbFileNames = kbFiles.length > 0
            ? kbFiles.map(f => f.name)
            : existingKbFileNames;
        const submitForGlobalReview = visibility === 'published';
        if (selectedType === 'agent' && !agentShareToken) {
            alert('Pick an agent and click Update before saving.');
            setSaving(false);
            return;
        }
        try {
            const payload = {
                id: editingId ? Number(editingId) : undefined,
                type: TOOL_TYPE_TO_INT[selectedType],
                name: name.trim(),
                category,
                icon: icon.trim(),
                description: description.trim(),
                skills: skills.trim(),
                kbFileNames,
                visibility: VISIBILITY_TO_INT[submitForGlobalReview ? 'just_me' : visibility],
                ...(selectedType === 'mcp_server' ? {
                    mcpUrl: mcpUrl.trim(),
                    mcpHeadersTemplate: mcpHeaders
                        .filter(h => h.key.trim())
                        .map(h => ({ [h.key.trim()]: h.requiredFromInstaller ? '' : h.value })),
                    configSchema: mcpHeaders
                        .filter(h => h.key.trim())
                        .map(h => ({
                        key: h.key.trim(), label: h.label?.trim() || h.key.trim(), type: 'text' as const,
                        requiredFromInstaller: h.requiredFromInstaller, hint: h.hint?.trim() || undefined,
                        scope: h.requiredFromInstaller ? (h.scope || 'shared') : undefined,
                    })),
                } : {}),
                ...(selectedType === 'skill' ? {
                    skillContent: skillContent.trim(),
                } : {}),
                ...(selectedType === 'agent' ? {
                    agentShareToken,
                } : {}),
            };
            const savedId = await apiUpsertTool(payload);
            const savedVisibility: Visibility = submitForGlobalReview ? 'just_me' : visibility;
            if (selectedType === 'skill' && kbFiles.length > 0) {
                const failures: string[] = [];
                for (const file of kbFiles) {
                    try {
                        await apiUploadSkillKbFile(savedId, file);
                    }
                    catch (err: any) {
                        failures.push(file.name);
                    }
                }
                setKbFiles([]);
                if (failures.length) {
                    alert(`Saved, but failed to upload: ${failures.join(', ')}. You can retry from the tool's detail page.`);
                }
            }
            if (submitForGlobalReview) {
                try {
                    await apiSubmitPublishRequest(String(savedId));
                }
                catch (err: any) {
                    alert(err.message === 'already_pending'
                        ? 'Saved. This tool already has a pending review request.'
                        : (err.message || 'Saved, but failed to submit for review. You can retry from Submit for Review.'));
                }
            }
            if (editingId) {
                setTools(prev => prev.map(t => t.id === editingId
                    ? {
                        ...t, type: selectedType, name: name.trim(), category, icon: icon.trim(), description: description.trim(),
                        skills: skills.trim(), kbFileNames, visibility: savedVisibility,
                        skillContent: selectedType === 'skill' ? skillContent.trim() : t.skillContent,
                        publishStatus: submitForGlobalReview ? 'pending' : t.publishStatus,
                    }
                    : t));
            }
            else {
                const fresh = await apiFetchMyTools();
                setTools(fresh);
                const created = fresh.find(t => t.name === name.trim() && t.id === savedId);
                if (created) { }
            }
            if (willAutoInstall) {
                handleInstall({ id: String(savedId), name: name.trim(), type: selectedType, icon: icon.trim(), mcpHeaders, visibility: savedVisibility, description: description.trim(), mcpUrl });
            }
            setSearchParams({ view: 'creations' });
        }
        catch (err: any) {
            alert(err.message || 'Failed to save tool');
        }
        finally {
            setSaving(false);
        }
    }, [
        selectedType, editingId, name, category, icon, description, skills, skillContent,
        kbFiles, existingKbFileNames, visibility, mcpUrl, mcpHeaders, agentShareToken,
        setSearchParams, handleInstall,
    ]);
    const handleDelete = useCallback(async (id: string) => {
        setTools(prev => prev.filter(t => t.id !== id));
        try {
            await apiDeleteTool(id);
        }
        catch (err: any) {
            alert(err.message || 'Failed to delete tool');
            apiFetchMyTools().then(setTools).catch(() => { });
        }
    }, []);
    const tabs: {
        key: WizardView;
        label: string;
    }[] = [
        { key: 'creations', label: 'My Creations' },
        { key: 'installed', label: 'Installed' },
        { key: 'marketplace', label: 'Browse' },
        { key: 'create', label: '+ Create New' },
    ];
    return (<div className="mt-page">
      <div className="mt-header">
        <h1 className="mt-header__title">Marketplace</h1>
        <p className="mt-header__subtitle">Browse tools, or build, publish, and manage your own on Momentum AMP.</p>
      </div>

      <div className="mt-tabs">
        {tabs.map(tab => (<button key={tab.key} type="button" className={`mt-tab ${view === tab.key && !(tab.key === 'create' && editingId) ? 'mt-tab--active' : ''}`} onClick={() => setSearchParams({ view: tab.key })}>
            {tab.key === 'create' && editingId ? 'Editing' : tab.label}
          </button>))}
      </div>

      {view === 'creations' && !selectedTool && (<CreationsView tools={tools} loading={loadingTools} onDelete={handleDelete} onEdit={handleEdit} onUpdate={handleUpdate} onInstall={handleInstall} installingId={installingCreationId} onUninstallForAgency={handleUninstallForAgency} onViewDetail={setSelectedTool} onRefetchTools={() => { apiFetchMyTools().then(setTools).catch(() => { }); }}/>)}

      <AnimatePresence>
        {pendingCreationInstall && (<MarketplaceInstallModal tool={pendingCreationInstall} mode="personal" installing={installingCreationId === pendingCreationInstall.id} onCancel={() => setPendingCreationInstall(null)} onConfirm={values => runCreationInstall(pendingCreationInstall, values)}/>)}
      </AnimatePresence>

      {view === 'installed' && <InstalledView />}

      {view === 'marketplace' && <MarketplaceView />}

      <AnimatePresence mode="wait">
        {view === 'creations' && selectedTool && (<ToolDetailPanel key={selectedTool.id} tool={selectedTool} onClose={() => setSelectedTool(null)}/>)}
      </AnimatePresence>

      <AnimatePresence>
        {publishToast && (<motion.div className={publishToast.type === 'success' ? 'success-toast' : 'error-toast'} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.25 }}>
            <span>{publishToast.message}</span>
          </motion.div>)}
      </AnimatePresence>

      {view === 'create' && (<div className="mt-wizard">
          <StepIndicator current={wizardStep}/>

          <AnimatePresence mode="wait">
            {wizardStep === 1 && (<Step1Type key="step1" selected={selectedType} onSelect={setSelectedType} onNext={() => setWizardStep(2)}/>)}
            {wizardStep === 2 && selectedType && (<Step2Info key="step2" selectedType={selectedType} name={name} setName={setName} category={category} setCategory={setCategory} icon={icon} setIcon={setIcon} description={description} setDescription={setDescription} skills={skills} setSkills={setSkills} skillContent={skillContent} setSkillContent={setSkillContent} kbFiles={kbFiles} setKbFiles={setKbFiles} editingId={editingId} existingSkillKbFiles={existingSkillKbFiles} setExistingSkillKbFiles={setExistingSkillKbFiles} visibility={visibility} setVisibility={setVisibility} mcpUrl={mcpUrl} setMcpUrl={setMcpUrl} mcpHeaders={mcpHeaders} setMcpHeaders={setMcpHeaders} agentId={agentId} setAgentId={setAgentId} agentShareToken={agentShareToken} setAgentShareToken={setAgentShareToken} agentPreview={agentPreview} setAgentPreview={setAgentPreview} agentFrozenSnapshot={agentFrozenSnapshot} setAgentFrozenSnapshot={setAgentFrozenSnapshot} onBack={() => editingId ? setSearchParams({ view: 'creations' }) : setWizardStep(1)} onNext={() => setWizardStep(3)}/>)}
            {wizardStep === 3 && selectedType && (<Step3Review key="step3" tool={{
                    type: selectedType, name, category, icon, description, skills,
                    kbFileNames: kbFiles.length > 0 ? kbFiles.map(f => f.name) : existingKbFileNames,
                    visibility, agentShareToken,
                }} agentPreview={agentPreview} onBack={() => setWizardStep(2)} onSave={() => handleSave()} isEditing={!!editingId} saving={saving}/>)}
          </AnimatePresence>
        </div>)}
    </div>);
};
export default MyToolsPage;
